import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { 
  Lock, Eye, EyeOff, Package, Users, Tag, DollarSign, 
  Ship, Plus, Trash2, Check, X, BarChart3, LogOut,
  RefreshCw, Search, Copy, Mail, Crown, Edit2, Download, AlertTriangle,
  Clock, CreditCard, Building2, CheckCircle, XCircle, FileText, ExternalLink,
  Factory, Upload, Image as ImageIcon, ChevronRight, Send, FileDown, Building, Star, ShieldCheck
} from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const ADMIN_PASSWORD = 'BlueCrate2026';

function Admin() {
  const navigate = useNavigate();
  const [authenticated, setAuthenticated] = useState(false);
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [activeTab, setActiveTab] = useState('dashboard');
  const [loading, setLoading] = useState(false);
  const [showResetConfirm, setShowResetConfirm] = useState(false);
  const [resetConfirmText, setResetConfirmText] = useState('');
  const [resetting, setResetting] = useState(false);

  // Data states
  const [stats, setStats] = useState(null);
  const [coupons, setCoupons] = useState([]);
  const [products, setProducts] = useState([]);
  const [pledges, setPledges] = useState([]);
  const [containers, setContainers] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [users, setUsers] = useState([]);
  const [members, setMembers] = useState([]);
  const [pendingPayments, setPendingPayments] = useState([]);
  const [authorizations, setAuthorizations] = useState([]);
  const [processingAuth, setProcessingAuth] = useState(null);
  const [verifyingPayment, setVerifyingPayment] = useState(null);
  const [deletingOrder, setDeletingOrder] = useState(null);
  const [taxExemptPending, setTaxExemptPending] = useState([]);
  const [verifyingTaxExempt, setVerifyingTaxExempt] = useState(null);

  // Product management
  const [showProductForm, setShowProductForm] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [productForm, setProductForm] = useState({
    name: '', internal_sku: '', upc_code: '', description: '', category: 'spc',
    unit: 'sqft',
    base_price: '', member_base_price: '', cost_price: '', retail_price: '',
    tier_pricing: { non_member: '', basic: '', pro: '', elite: '' },
    specifications: { dimensions: '', thickness: '', wear_layer: '' },
    factory_id: '', sqft_per_box: '', pieces_per_box: ''
  });
  const [uploadingProductImage, setUploadingProductImage] = useState(false);

  // Factory management
  const [factories, setFactories] = useState([]);
  const [showFactoryForm, setShowFactoryForm] = useState(false);
  const [editingFactory, setEditingFactory] = useState(null);
  const [factoryForm, setFactoryForm] = useState({
    name: '', contact_name: '', email: '', phone: '', location: '', notes: ''
  });

  // Warehouse management - removed (not needed, orders go direct to customers)

  // Container status management
  const [updatingContainerStatus, setUpdatingContainerStatus] = useState(null);
  const [editingContainer, setEditingContainer] = useState(null);
  const [containerEditForm, setContainerEditForm] = useState({});
  const [containerStatusForm, setContainerStatusForm] = useState({
    status: '', factory_id: '', tracking_number: '', notes: '', estimated_delivery: ''
  });
  const [poPreview, setPoPreview] = useState(null);
  const [sendingPO, setSendingPO] = useState(null);
  const [assigningFactory, setAssigningFactory] = useState(null);
  const [syncingPrices, setSyncingPrices] = useState(false);

  // New coupon form
  const [newCoupon, setNewCoupon] = useState({
    code: '',
    membership_tier: 'elite',
    is_multi_use: true,
    usage_limit: null,
    description: ''
  });

  // Edit user modal
  const [editingUser, setEditingUser] = useState(null);
  const [deletingUser, setDeletingUser] = useState(null);

  // Edit order modal
  const [editingOrder, setEditingOrder] = useState(null);
  const [editOrderForm, setEditOrderForm] = useState({ 
    sqft: '', 
    deposit_paid: '',
    price_per_sqft: '',
    company_name: '',
    company_address: '',
    company_phone: '',
    company_tax_id: ''
  });
  const [savingOrder, setSavingOrder] = useState(false);
  const [sendingInvoice, setSendingInvoice] = useState(null);
  const [orderActionsOpen, setOrderActionsOpen] = useState(null);
  const [generatingUPC, setGeneratingUPC] = useState(null);

  // Restore admin session on mount
  useEffect(() => {
    if (localStorage.getItem('bluecrate_admin_auth') === 'true') {
      setAuthenticated(true);
      fetchAllData();
    }
  }, []); // eslint-disable-line

  const handleLogin = (e) => {
    e.preventDefault();
    if (password === ADMIN_PASSWORD) {
      setAuthenticated(true);
      localStorage.setItem('bluecrate_admin_auth', 'true');
      toast.success('Welcome to BlueCrate Admin');
      fetchAllData();
    } else {
      toast.error('Incorrect password');
    }
  };

  const handleLogout = () => {
    setAuthenticated(false);
    localStorage.removeItem('bluecrate_admin_auth');
    setPassword('');
    navigate('/');
  };

  const fetchAllData = async () => {
    setLoading(true);
    try {
      const [productsRes, containersRes, pledgesRes, couponsRes, transactionsRes, usersRes, statsRes, pendingRes, taxExemptRes, factoriesRes, membersRes, authorizationsRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/products`).catch(e => { console.error('products failed:', e.message); return { data: [] }; }),
        axios.get(`${BACKEND_URL}/api/containers`).catch(e => { console.error('containers failed:', e.message); return { data: [] }; }),
        axios.get(`${BACKEND_URL}/api/admin/pledges`).catch(e => { console.error('pledges failed:', e.message); return { data: [] }; }),
        axios.get(`${BACKEND_URL}/api/admin/coupons`).catch(e => { console.error('coupons failed:', e.message); return { data: [] }; }),
        axios.get(`${BACKEND_URL}/api/admin/transactions`).catch(e => { console.error('transactions failed:', e.message); return { data: [] }; }),
        axios.get(`${BACKEND_URL}/api/admin/users`).catch(e => { console.error('users failed:', e.message); return { data: [] }; }),
        axios.get(`${BACKEND_URL}/api/admin/stats`).catch(e => { console.error('stats failed:', e.message); return { data: {} }; }),
        axios.get(`${BACKEND_URL}/api/admin/pending-payments`).catch(e => { console.error('pending-payments failed:', e.message); return { data: { pending_orders: [] } }; }),
        axios.get(`${BACKEND_URL}/api/admin/tax-exempt-pending`).catch(e => { console.error('tax-exempt failed:', e.message); return { data: { pending_tax_exempt: [] } }; }),
        axios.get(`${BACKEND_URL}/api/admin/factories`).catch(e => { console.error('factories failed:', e.message); return { data: [] }; }),
        axios.get(`${BACKEND_URL}/api/admin/members`).catch(e => { console.error('members failed:', e.message); return { data: [] }; }),
        axios.get(`${BACKEND_URL}/api/admin/payments/authorizations?admin_password=${encodeURIComponent(ADMIN_PASSWORD)}`).catch(e => { console.error('authorizations failed:', e.message); return { data: { authorizations: [] } }; })
      ]);

      setProducts(productsRes.data);
      setContainers(containersRes.data);
      setPledges(pledgesRes.data);
      setCoupons(couponsRes.data);
      setTransactions(transactionsRes.data);
      setUsers(usersRes.data);
      setPendingPayments(pendingRes.data.pending_orders || []);
      setTaxExemptPending(taxExemptRes.data.pending_tax_exempt || []);
      setFactories(factoriesRes.data || []);
      setMembers(membersRes.data || []);
      setAuthorizations(authorizationsRes.data.authorizations || []);

      // Use stats from dedicated endpoint
      const statsData = statsRes?.data || {};
      setStats({
        totalRevenue: statsData.total_revenue || 0,
        totalPledges: statsData.total_pledges || 0,
        activeCoupons: statsData.active_coupons || 0,
        containerFill: statsData.container_fill || 0,
        containerCapacity: statsData.container_capacity || 26900,
        fill_percentage: statsData.fill_percentage || 0,
        container_number: statsData.container_number,
        production_triggered: statsData.production_triggered || false,
        total_sqft_sold: statsData.total_sqft_sold || 0,
        total_cost: statsData.total_cost || 0,
        estimated_profit: statsData.estimated_profit || 0,
        business_cost_per_sqft: statsData.business_cost_per_sqft || 0.96
      });

    } catch (error) {
      console.error('Unexpected error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleResetAllData = async () => {
    if (resetConfirmText !== 'RESET') {
      toast.error('Please type RESET to confirm');
      return;
    }

    setResetting(true);
    try {
      const response = await axios.post(`${BACKEND_URL}/api/admin/reset-all-data`, {
        confirm: 'RESET_ALL_DATA'
      });
      
      toast.success(`Data reset complete! Deleted ${response.data.deleted.pledges} pledges, ${response.data.deleted.containers} containers, ${response.data.deleted.transactions} transactions`);
      setShowResetConfirm(false);
      setResetConfirmText('');
      
      // Refresh all data
      fetchAllData();
    } catch (error) {
      console.error('Error resetting data:', error);
      toast.error(error.response?.data?.detail || 'Failed to reset data');
    } finally {
      setResetting(false);
    }
  };

  const createCoupon = async (e) => {
    e.preventDefault();
    if (!newCoupon.code) {
      toast.error('Please enter a coupon code');
      return;
    }

    try {
      const response = await axios.post(`${BACKEND_URL}/api/admin/coupons`, {
        code: newCoupon.code.toUpperCase(),
        membership_tier: newCoupon.membership_tier,
        is_multi_use: newCoupon.is_multi_use,
        usage_limit: newCoupon.is_multi_use ? null : parseInt(newCoupon.usage_limit) || 1,
        description: newCoupon.description,
        is_active: true
      });

      toast.success(`Coupon ${newCoupon.code.toUpperCase()} created!`);
      setCoupons([...coupons, response.data]);
      setNewCoupon({
        code: '',
        membership_tier: 'elite',
        is_multi_use: true,
        usage_limit: null,
        description: ''
      });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create coupon');
    }
  };

  const toggleCouponStatus = async (couponCode, currentStatus) => {
    try {
      await axios.patch(`${BACKEND_URL}/api/admin/coupons/${couponCode}`, {
        is_active: !currentStatus
      });
      setCoupons(coupons.map(c => 
        c.code === couponCode ? { ...c, is_active: !currentStatus } : c
      ));
      toast.success(`Coupon ${currentStatus ? 'deactivated' : 'activated'}`);
    } catch (error) {
      toast.error('Failed to update coupon');
    }
  };

  const handleVerifyPayment = async (pledgeId, verified) => {
    setVerifyingPayment(pledgeId);
    try {
      const response = await axios.post(`${BACKEND_URL}/api/admin/orders/${pledgeId}/verify-payment`, {
        verified: verified,
        notes: verified ? 'Payment verified by admin' : 'Payment not received'
      });
      
      if (verified) {
        toast.success('Payment verified! Order is now active.');
      } else {
        toast.success('Order cancelled.');
      }
      
      // Remove from pending list
      setPendingPayments(pendingPayments.filter(p => p.pledge_id !== pledgeId));
      
      // Refresh all data to update other sections
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to verify payment');
    } finally {
      setVerifyingPayment(null);
    }
  };

  const handleDeleteOrder = async (pledgeId) => {
    if (!window.confirm(`Are you sure you want to delete order ${pledgeId}? This cannot be undone.`)) {
      return;
    }
    
    setDeletingOrder(pledgeId);
    try {
      await axios.delete(`${BACKEND_URL}/api/admin/orders/${pledgeId}`);
      toast.success('Order deleted successfully');
      
      // Remove from local state
      setPledges(pledges.filter(p => p.pledge_id !== pledgeId));
      setPendingPayments(pendingPayments.filter(p => p.pledge_id !== pledgeId));
      
      // Refresh all data
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete order');
    } finally {
      setDeletingOrder(null);
    }
  };

  const handleEditOrder = (pledge) => {
    setEditingOrder(pledge);
    const companyInfo = pledge.company_info || {};
    // For piece-unit pledges, pledge.sqft is stored as 0 by the backend. Prefer the actual
    // quantity from line_items[0] so the modal doesn't zero out the order on save.
    const firstLi = pledge.line_items?.[0];
    const isPieceOrder = (firstLi?.unit || '').toLowerCase() === 'piece';
    const initialQty = isPieceOrder
      ? (firstLi.quantity ?? firstLi.sqft ?? 0)
      : (pledge.sqft ?? 0);
    setEditOrderForm({
      price_per_sqft: pledge.price_per_sqft?.toString() || '',
      sqft: initialQty?.toString() || '',
      deposit_paid: pledge.deposit_paid?.toString() || '0',
      company_name: companyInfo.company_name || '',
      company_address: companyInfo.address || '',
      company_phone: companyInfo.phone || '',
      company_tax_id: companyInfo.tax_id || ''
    });
  };

  const handleSaveOrderEdit = async () => {
    if (!editingOrder) return;
    
    const newSqft = parseFloat(editOrderForm.sqft);
    const newDepositPaid = parseFloat(editOrderForm.deposit_paid);
    const newPricePerSqft = parseFloat(editOrderForm.price_per_sqft);
    
    if (isNaN(newSqft) || newSqft <= 0) {
      toast.error('Please enter a valid square footage');
      return;
    }
    if (isNaN(newDepositPaid) || newDepositPaid < 0) {
      toast.error('Please enter a valid paid amount');
      return;
    }
    if (editOrderForm.price_per_sqft && (isNaN(newPricePerSqft) || newPricePerSqft <= 0)) {
      toast.error('Please enter a valid price per sqft');
      return;
    }
    
    setSavingOrder(true);
    try {
      const payload = { 
        sqft: newSqft, 
        deposit_paid: newDepositPaid,
        company_info: {
          company_name: editOrderForm.company_name,
          address: editOrderForm.company_address,
          phone: editOrderForm.company_phone,
          tax_id: editOrderForm.company_tax_id
        }
      };
      
      // Only add price_per_sqft if it was changed
      if (editOrderForm.price_per_sqft && newPricePerSqft !== editingOrder.price_per_sqft) {
        payload.price_per_sqft = newPricePerSqft;
      }
      
      const response = await axios.put(
        `${BACKEND_URL}/api/admin/orders/${editingOrder.pledge_id}/details`,
        payload
      );
      
      toast.success('Order updated! Customer will receive an email notification.');
      
      // Update local state
      setPledges(pledges.map(p => 
        p.pledge_id === editingOrder.pledge_id 
          ? { ...p, ...response.data.updated_pledge }
          : p
      ));
      
      setEditingOrder(null);
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update order');
    } finally {
      setSavingOrder(false);
    }
  };

  const handleDownloadInvoice = (pledge) => {
    const url = `${BACKEND_URL}/api/orders/${pledge.pledge_id}/invoice`;
    window.open(url, '_blank');
    setOrderActionsOpen(null);
  };

  const handleSendInvoice = async (pledge) => {
    setSendingInvoice(pledge.pledge_id);
    setOrderActionsOpen(null);
    try {
      await axios.post(`${BACKEND_URL}/api/admin/orders/${pledge.pledge_id}/send-invoice`);
      toast.success(`Invoice sent to ${pledge.user_email}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send invoice');
    } finally {
      setSendingInvoice(null);
    }
  };

  const handleSendReminder = async (pledge) => {
    setOrderActionsOpen(null);
    try {
      const response = await axios.post(`${BACKEND_URL}/api/admin/orders/${pledge.pledge_id}/send-reminder`);
      if (response.data.success) {
        toast.success(`Payment reminder sent to ${pledge.user_email}`);
      } else {
        toast.info(response.data.message);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send reminder');
    }
  };

  const handleVerifyTaxExempt = async (pledgeId, approved, notes = '') => {
    setVerifyingTaxExempt(pledgeId);
    try {
      await axios.post(`${BACKEND_URL}/api/admin/orders/${pledgeId}/verify-tax-exempt`, {
        approved,
        notes: notes || (approved ? 'Approved by admin' : 'Rejected by admin')
      });
      toast.success(approved ? 'Tax exempt status approved!' : 'Tax exempt status rejected.');
      setTaxExemptPending(taxExemptPending.filter(p => p.pledge_id !== pledgeId));
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update tax exempt status');
    } finally {
      setVerifyingTaxExempt(null);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard');
  };

  // ---- Product CRUD ----
  const resetProductForm = () => {
    setProductForm({
      name: '', internal_sku: '', upc_code: '', description: '', category: 'spc',
      unit: 'sqft',
      base_price: '', member_base_price: '', cost_price: '', retail_price: '',
      tier_pricing: { non_member: '', basic: '', pro: '', elite: '' },
      specifications: { dimensions: '', thickness: '', wear_layer: '' },
      factory_id: ''
    });
    setEditingProduct(null);
    setShowProductForm(false);
  };

  const openEditProduct = (product) => {
    setProductForm({
      name: product.name || '',
      internal_sku: product.internal_sku || '',
      upc_code: product.upc_code || '',
      description: product.description || '',
      category: product.category || 'spc',
      unit: product.unit || 'sqft',
      base_price: product.base_price?.toString() || '',
      member_base_price: product.member_base_price?.toString() || '',
      cost_price: product.cost_price?.toString() || '',
      retail_price: product.retail_price?.toString() || '',
      tier_pricing: {
        non_member: product.base_price?.toString() || '',
        basic: product.member_pricing?.member?.toString() || product.member_base_price?.toString() || '',
        pro: product.member_pricing?.member?.toString() || product.member_base_price?.toString() || '',
        elite: product.member_pricing?.member?.toString() || product.member_base_price?.toString() || ''
      },
      specifications: product.specifications || { dimensions: '', thickness: '', wear_layer: '' },
      factory_id: product.factory_id || '',
      sqft_per_box: product.sqft_per_box?.toString() || '',
      pieces_per_box: product.pieces_per_box?.toString() || ''
    });
    setEditingProduct(product);
    setShowProductForm(true);
  };

  // Sync product prices to correct values (single-tier membership pricing)
  const handleSyncPrices = async () => {
    if (syncingPrices) return;
    
    const confirmed = window.confirm(
      'This will update ALL product prices to:\n\n' +
      '• Hardwood: $3.56 (non-member) / $2.49 (member), 23.33 sqft/box\n' +
      '• LVP/SPC: $2.24 (non-member) / $1.23 (member), 37.78 sqft/box\n\n' +
      'Continue?'
    );
    
    if (!confirmed) return;
    
    setSyncingPrices(true);
    try {
      const response = await axios.post(`${BACKEND_URL}/api/admin/products/bulk-update-prices`, {
        admin_password: ADMIN_PASSWORD,
        updates: [
          { category: 'engineered_hardwood', base_price: 3.56, member_base_price: 2.49, member_pricing: { member: 2.49 }, sqft_per_box: 23.33 },
          { category: 'solid_hardwood', base_price: 3.56, member_base_price: 2.49, member_pricing: { member: 2.49 }, sqft_per_box: 23.33 },
          { category: 'spc', base_price: 2.24, member_base_price: 1.23, member_pricing: { member: 1.23 }, sqft_per_box: 37.78 }
        ]
      });
      
      if (response.data.success) {
        toast.success('Product prices synced successfully!');
        fetchAllData(); // Refresh data
      }
    } catch (error) {
      console.error('Price sync error:', error);
      toast.error(error.response?.data?.detail || 'Failed to sync prices');
    } finally {
      setSyncingPrices(false);
    }
  };

  const handleBulkAdjustPrices = async () => {
    const input = window.prompt(
      'Adjust ALL product prices by how much per sqft?\n\n' +
      'Enter a positive number to raise prices, or a negative number to lower.\n' +
      'Example: 0.21 → adds $0.21 to every price field (non-member, member, retail, tiers).\n\n' +
      'This affects EVERY product. Continue by entering a value:'
    );
    if (input === null || input === '') return;
    const delta = parseFloat(input);
    if (isNaN(delta)) { toast.error('Please enter a valid number'); return; }

    const confirmed = window.confirm(
      `Apply ${delta >= 0 ? '+' : ''}$${delta.toFixed(2)}/sqft to ALL products and every tier?\n\n` +
      'This runs immediately and cannot be undone. Are you sure?'
    );
    if (!confirmed) return;

    setSyncingPrices(true);
    try {
      const response = await axios.post(`${BACKEND_URL}/api/admin/products/bulk-increment-prices`, {
        admin_password: ADMIN_PASSWORD,
        delta,
      });
      if (response.data.success) {
        toast.success(`Prices adjusted by ${delta >= 0 ? '+' : ''}$${delta.toFixed(2)} on ${response.data.products_updated.length} products`);
        fetchAllData();
      }
    } catch (error) {
      console.error('Price adjust error:', error);
      toast.error(error.response?.data?.detail || 'Failed to adjust prices');
    } finally {
      setSyncingPrices(false);
    }
  };

  const handleSaveProduct = async (e) => {
    e.preventDefault();
    if (!productForm.name) { toast.error('Product display name is required'); return; }
    
    const payload = {
      ...productForm,
      base_price: parseFloat(productForm.base_price) || 0,
      member_base_price: parseFloat(productForm.member_base_price) || 0,
      cost_price: parseFloat(productForm.cost_price) || 0,
      retail_price: productForm.retail_price ? parseFloat(productForm.retail_price) : null,
      upc_code: productForm.upc_code || null,
      sqft_per_box: productForm.sqft_per_box ? parseFloat(productForm.sqft_per_box) : null,
      pieces_per_box: productForm.pieces_per_box ? parseInt(productForm.pieces_per_box) : null,
      tier_pricing: {
        non_member: parseFloat(productForm.tier_pricing.non_member) || parseFloat(productForm.base_price) || 0,
        basic: parseFloat(productForm.tier_pricing.basic) || 0,
        pro: parseFloat(productForm.tier_pricing.pro) || 0,
        elite: parseFloat(productForm.tier_pricing.elite) || parseFloat(productForm.member_base_price) || 0
      }
    };

    try {
      if (editingProduct) {
        await axios.put(`${BACKEND_URL}/api/admin/products/${editingProduct.product_id}`, payload);
        toast.success('Product updated!');
      } else {
        await axios.post(`${BACKEND_URL}/api/admin/products`, payload);
        toast.success('Product created!');
      }
      resetProductForm();
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save product');
    }
  };

  const handleDeleteProduct = async (productId) => {
    if (!window.confirm('Delete this product? This cannot be undone.')) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/admin/products/${productId}`);
      toast.success('Product deleted');
      setProducts(products.filter(p => p.product_id !== productId));
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete product');
    }
  };

  const handleProductImageUpload = async (productId, file) => {
    if (!file) return;
    setUploadingProductImage(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      await axios.post(`${BACKEND_URL}/api/admin/products/${productId}/upload-image`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success('Image uploaded!');
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload image');
    } finally {
      setUploadingProductImage(false);
    }
  };

  const handleGenerateUPC = async (productId) => {
    setGeneratingUPC(productId);
    try {
      const response = await axios.post(`${BACKEND_URL}/api/admin/products/${productId}/generate-upc`);
      if (response.data.success) {
        toast.success(`UPC generated: ${response.data.upc_code}`);
        // Update local product form if editing
        if (editingProduct?.product_id === productId) {
          setProductForm(f => ({ ...f, upc_code: response.data.upc_code }));
        }
        fetchAllData();
      } else {
        toast.info(response.data.message);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to generate UPC');
    } finally {
      setGeneratingUPC(null);
    }
  };

  const handleDownloadBarcode = (productId, type = 'barcode') => {
    const endpoint = type === 'label' 
      ? `${BACKEND_URL}/api/admin/products/${productId}/barcode-label`
      : `${BACKEND_URL}/api/admin/products/${productId}/barcode`;
    window.open(endpoint, '_blank');
  };

  const handleDeleteProductImage = async (productId, imageUrl) => {
    try {
      await axios.delete(`${BACKEND_URL}/api/admin/products/${productId}/images`, {
        data: { image_url: imageUrl }
      });
      toast.success('Image removed');
      fetchAllData();
    } catch (error) {
      toast.error('Failed to remove image');
    }
  };

  // ---- Factory CRUD ----
  const resetFactoryForm = () => {
    setFactoryForm({ name: '', contact_name: '', email: '', phone: '', location: '', notes: '' });
    setEditingFactory(null);
    setShowFactoryForm(false);
  };

  const openEditFactory = (factory) => {
    setFactoryForm({
      name: factory.name || '', contact_name: factory.contact_name || '',
      email: factory.email || '', phone: factory.phone || '',
      location: factory.location || '', notes: factory.notes || ''
    });
    setEditingFactory(factory);
    setShowFactoryForm(true);
  };

  const handleSaveFactory = async (e) => {
    e.preventDefault();
    if (!factoryForm.name) { toast.error('Factory name is required'); return; }
    try {
      if (editingFactory) {
        await axios.put(`${BACKEND_URL}/api/admin/factories/${editingFactory.factory_id}`, factoryForm);
        toast.success('Factory updated!');
      } else {
        await axios.post(`${BACKEND_URL}/api/admin/factories`, factoryForm);
        toast.success('Factory added!');
      }
      resetFactoryForm();
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save factory');
    }
  };

  const handleDeleteFactory = async (factoryId) => {
    if (!window.confirm('Delete this factory?')) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/admin/factories/${factoryId}`);
      toast.success('Factory deleted');
      setFactories(factories.filter(f => f.factory_id !== factoryId));
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete factory');
    }
  };

  const getFactoryName = (factoryId) => {
    const f = factories.find(f => f.factory_id === factoryId);
    return f ? f.name : '—';
  };

  // ---- PO Preview & CSV Export ----
  const handleSaveContainerEdit = async () => {
    if (!editingContainer) return;
    try {
      await axios.put(`${BACKEND_URL}/api/admin/containers/${editingContainer.container_id}`, containerEditForm);
      toast.success('Container updated');
      setEditingContainer(null);
      fetchAllData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to update container');
    }
  };

  const handleAssignFactory = async (containerId, factoryId) => {
    setAssigningFactory(containerId);
    try {
      await axios.put(`${BACKEND_URL}/api/admin/containers/${containerId}/assign-factory`, {
        factory_id: factoryId || null
      });
      toast.success(factoryId ? 'Factory assigned to container!' : 'Factory removed from container');
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to assign factory');
    } finally {
      setAssigningFactory(null);
    }
  };

  const loadPoPreview = async (containerId) => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/admin/containers/${containerId}/po-preview`);
      setPoPreview({ containerId, ...res.data });
    } catch (error) {
      toast.error('Failed to load PO preview');
    }
  };

  const downloadPoCsv = (containerId) => {
    if (!poPreview || poPreview.containerId !== containerId) return;

    let csv = 'Internal SKU,Total Sqft\n';
    (poPreview.line_items || []).forEach(item => {
      csv += `"${item.internal_sku || 'N/A'}",${item.total_sqft}\n`;
    });
    csv += `\n"TOTAL",${poPreview.total_sqft}\n`;

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    const containerNum = poPreview.container_number || containerId;
    a.download = `PO_${containerNum}_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success('CSV downloaded!');
  };

  // ---- Container Status ----
  const handleSendPO = async (containerId) => {
    if (!containerStatusForm.factory_id) {
      toast.error('Please select a factory before sending PO');
      return;
    }
    setSendingPO(containerId);
    try {
      const res = await axios.post(`${BACKEND_URL}/api/admin/containers/${containerId}/send-po`, {
        factory_id: containerStatusForm.factory_id,
        notes: containerStatusForm.notes
      });
      toast.success(`PO sent! ${res.data.emails_sent} customer(s) notified${res.data.supplier_notified ? ' + supplier emailed' : ''}.`);
      setContainerStatusForm({ status: '', factory_id: '', tracking_number: '', notes: '', estimated_delivery: '' });
      setPoPreview(null);
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send PO');
    } finally {
      setSendingPO(null);
    }
  };

  const [resendingPO, setResendingPO] = useState(null);
  const handleResendPO = async (containerId) => {
    setResendingPO(containerId);
    try {
      const res = await axios.post(`${BACKEND_URL}/api/admin/containers/${containerId}/resend-po-email`);
      toast.success(`PO email resent to ${res.data.factory_name} (${res.data.sent_to})`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to resend PO email');
    } finally {
      setResendingPO(null);
    }
  };

  const [showCreateUser, setShowCreateUser] = useState(false);
  const [createUserForm, setCreateUserForm] = useState({ name: '', email: '', phone: '', membership_tier: 'none', address: { street: '', city: '', state: '', zip: '' } });
  const [creatingUser, setCreatingUser] = useState(false);
  const [createdUserCreds, setCreatedUserCreds] = useState(null);

  const handleCreateUser = async () => {
    if (!createUserForm.email) { toast.error('Email is required'); return; }
    setCreatingUser(true);
    try {
      const res = await axios.post(`${BACKEND_URL}/api/admin/create-user`, createUserForm);
      setCreatedUserCreds({ email: res.data.email, temp_password: res.data.temp_password });
      toast.success(res.data.message);
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create account');
    } finally {
      setCreatingUser(false);
    }
  };

  const resetCreateUserForm = () => {
    setCreateUserForm({ name: '', email: '', phone: '', membership_tier: 'none', address: { street: '', city: '', state: '', zip: '' } });
    setCreatedUserCreds(null);
    setShowCreateUser(false);
  };

  // Create Order state
  const [showCreateOrder, setShowCreateOrder] = useState(false);
  const [createOrderForm, setCreateOrderForm] = useState({
    email: '',
    line_items: [{ product_id: '', sqft: '', price_per_sqft: '' }],
    payment_status: 'unpaid',
    container_mode: 'auto', container_id: '',
    shipping_address: { street: '', city: '', state: '', zip: '' }, notes: ''
  });
  const [creatingOrder, setCreatingOrder] = useState(false);
  const [createdOrder, setCreatedOrder] = useState(null);

  const addOrderLineItem = () => {
    setCreateOrderForm(f => ({ ...f, line_items: [...f.line_items, { product_id: '', sqft: '', price_per_sqft: '' }] }));
  };
  const removeOrderLineItem = (idx) => {
    setCreateOrderForm(f => ({ ...f, line_items: f.line_items.length <= 1 ? f.line_items : f.line_items.filter((_, i) => i !== idx) }));
  };
  const updateOrderLineItem = (idx, patch) => {
    setCreateOrderForm(f => ({ ...f, line_items: f.line_items.map((li, i) => i === idx ? { ...li, ...patch } : li) }));
  };

  const handleCreateOrder = async () => {
    if (!createOrderForm.email) { toast.error('Customer email is required'); return; }
    const validItems = createOrderForm.line_items.filter(li => li.product_id && parseFloat(li.sqft) > 0);
    if (validItems.length === 0) { toast.error('Add at least one product with sqft'); return; }

    setCreatingOrder(true);
    try {
      const payload = {
        email: createOrderForm.email,
        line_items: validItems,
        payment_status: createOrderForm.payment_status,
        container_mode: createOrderForm.container_mode,
        container_id: createOrderForm.container_id,
        shipping_address: createOrderForm.shipping_address,
        notes: createOrderForm.notes,
      };
      const res = await axios.post(`${BACKEND_URL}/api/admin/create-order`, payload);
      setCreatedOrder(res.data);
      toast.success(res.data.message);
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create order');
    } finally {
      setCreatingOrder(false);
    }
  };

  const resetCreateOrderForm = () => {
    setCreateOrderForm({
      email: '',
      line_items: [{ product_id: '', sqft: '', price_per_sqft: '' }],
      payment_status: 'unpaid',
      container_mode: 'auto', container_id: '',
      shipping_address: { street: '', city: '', state: '', zip: '' }, notes: ''
    });
    setCreatedOrder(null);
    setShowCreateOrder(false);
  };

  const handleUpdateContainerStatus = async (containerId, newStatus) => {
    setUpdatingContainerStatus(containerId);
    try {
      const res = await axios.put(`${BACKEND_URL}/api/admin/containers/${containerId}/status`, {
        status: newStatus,
        tracking_number: containerStatusForm.tracking_number,
        notes: containerStatusForm.notes,
        estimated_delivery: containerStatusForm.estimated_delivery
      });
      toast.success(`Status updated to "${newStatus.replace(/_/g, ' ')}". ${res.data.emails_sent} customer(s) notified.`);
      setContainerStatusForm({ status: '', factory_id: '', tracking_number: '', notes: '', estimated_delivery: '' });
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update status');
    } finally {
      setUpdatingContainerStatus(null);
    }
  };

  const STATUS_FLOW = [
    { key: 'filling', label: 'Filling', color: 'bg-gray-400' },
    { key: 'po_sent', label: 'PO Sent', color: 'bg-blue-500' },
    { key: 'in_production', label: 'In Production', color: 'bg-amber-500' },
    { key: 'production_complete', label: 'Production Complete', color: 'bg-orange-500' },
    { key: 'at_port', label: 'At Port', color: 'bg-cyan-500' },
    { key: 'on_vessel', label: 'On Vessel', color: 'bg-blue-600' },
    { key: 'arrived', label: 'Arrived', color: 'bg-green-500' },
    { key: 'unloaded', label: 'Unloaded', color: 'bg-cyan-600' },
    { key: 'customs_cleared', label: 'Customs Cleared', color: 'bg-emerald-500' },
    { key: 'on_rail', label: 'On Rail', color: 'bg-indigo-500' },
    { key: 'out_for_delivery', label: 'Out for Delivery', color: 'bg-orange-600' },
    { key: 'delivered', label: 'Delivered', color: 'bg-green-700' }
  ];

  if (!authenticated) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 to-gray-800 flex items-center justify-center p-4">
        <div className="bg-white rounded-3xl shadow-2xl p-8 w-full max-w-md">
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-primary/10 rounded-full mb-4">
              <Lock className="w-8 h-8 text-primary" />
            </div>
            <h1 className="text-2xl font-bold text-gray-900">Admin Access</h1>
            <p className="text-gray-600 mt-2">Enter the admin password to continue</p>
          </div>

          <form onSubmit={handleLogin} className="space-y-4">
            <div className="relative">
              <input
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter password"
                className="w-full p-4 pr-12 border-2 border-gray-200 rounded-xl focus:border-primary focus:outline-none text-lg"
                data-testid="admin-password-input"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>
            <Button
              type="submit"
              className="w-full h-12 rounded-xl bg-primary hover:bg-primary/90"
              data-testid="admin-login-btn"
            >
              Access Admin Panel
            </Button>
          </form>

          <button
            onClick={() => navigate('/')}
            className="w-full mt-4 text-gray-500 hover:text-gray-700 text-sm"
          >
            ← Back to Home
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Header */}
      <header className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex justify-between items-center">
            <div className="flex items-center space-x-4">
              <img src="/bluecrate-logo-cropped.jpg" alt="BlueCrate" className="h-10" />
              <span className="text-xl font-bold text-gray-900">Admin Panel</span>
            </div>
            <div className="flex items-center space-x-4">
              <Button
                onClick={fetchAllData}
                variant="outline"
                size="sm"
                disabled={loading}
                data-testid="refresh-data-btn"
              >
                <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                Refresh
              </Button>
              <Button
                onClick={handleLogout}
                variant="outline"
                size="sm"
                data-testid="admin-logout-btn"
              >
                <LogOut className="w-4 h-4 mr-2" />
                Logout
              </Button>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Tabs */}
        <div className="flex space-x-2 mb-8 overflow-x-auto pb-2">
          {[
            { id: 'dashboard', label: 'Dashboard', icon: BarChart3 },
            { id: 'pending', label: `Pending Payments${pendingPayments.length > 0 ? ` (${pendingPayments.length})` : ''}`, icon: Clock, highlight: pendingPayments.length > 0 },
            { id: 'authorizations', label: `Authorizations${authorizations.filter(a => a.status === 'AUTHORIZED').length > 0 ? ` (${authorizations.filter(a => a.status === 'AUTHORIZED').length})` : ''}`, icon: ShieldCheck, highlight: authorizations.filter(a => a.status === 'AUTHORIZED').length > 0 },
            { id: 'tax_exempt', label: `Tax Exempt${taxExemptPending.length > 0 ? ` (${taxExemptPending.length})` : ''}`, icon: FileText, highlight: taxExemptPending.length > 0 },
            { id: 'members', label: 'Members', icon: Star },
            { id: 'users', label: 'Users', icon: Users },
            { id: 'coupons', label: 'Coupons', icon: Tag },
            { id: 'pledges', label: 'Pledges', icon: Package },
            { id: 'profit', label: 'Profit', icon: DollarSign },
            { id: 'containers', label: 'Containers', icon: Ship },
            { id: 'products', label: 'Products', icon: Package },
            { id: 'factories', label: 'Factories', icon: Factory },
            { id: 'transactions', label: 'Transactions', icon: CreditCard }
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center px-4 py-2 rounded-lg font-medium transition-colors whitespace-nowrap ${
                activeTab === tab.id
                  ? 'bg-primary text-white'
                  : tab.highlight 
                    ? 'bg-amber-100 text-amber-800 hover:bg-amber-200 border-2 border-amber-400'
                    : 'bg-white text-gray-600 hover:bg-gray-50'
              }`}
              data-testid={`tab-${tab.id}`}
            >
              <tab.icon className="w-4 h-4 mr-2" />
              {tab.label}
            </button>
          ))}
        </div>

        {/* Pending Payments Tab */}
        {activeTab === 'pending' && (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <h2 className="text-2xl font-bold">Pending Payments</h2>
              <span className="text-gray-500">Wire transfers & checks awaiting verification</span>
            </div>

            {pendingPayments.length === 0 ? (
              <div className="bg-white rounded-2xl shadow p-12 text-center">
                <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
                <h3 className="text-xl font-bold text-gray-900 mb-2">All Caught Up!</h3>
                <p className="text-gray-600">No pending payments to verify.</p>
              </div>
            ) : (
              <div className="space-y-4">
                {pendingPayments.map((order) => {
                  const deadline = new Date(order.payment_deadline);
                  const now = new Date();
                  const hoursLeft = Math.max(0, Math.floor((deadline - now) / (1000 * 60 * 60)));
                  const isExpired = hoursLeft <= 0;
                  const isUrgent = hoursLeft <= 24 && !isExpired;

                  return (
                    <div 
                      key={order.pledge_id}
                      className={`bg-white rounded-2xl shadow p-6 border-2 ${
                        isExpired ? 'border-red-400 bg-red-50' : 
                        isUrgent ? 'border-amber-400' : 'border-gray-200'
                      }`}
                      data-testid={`pending-order-${order.pledge_id}`}
                    >
                      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                        {/* Order Info */}
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            {order.payment_method === 'wire' ? (
                              <Building2 className="w-6 h-6 text-blue-600" />
                            ) : (
                              <Mail className="w-6 h-6 text-blue-600" />
                            )}
                            <span className="font-bold text-lg">
                              {order.payment_method === 'wire' ? 'Wire Transfer' : 'Check by Mail'}
                            </span>
                            <span className={`px-2 py-0.5 rounded-full text-xs font-bold ${
                              isExpired ? 'bg-red-200 text-red-800' :
                              isUrgent ? 'bg-amber-200 text-amber-800' :
                              'bg-blue-100 text-blue-800'
                            }`}>
                              {isExpired ? 'EXPIRED' : `${hoursLeft}h left`}
                            </span>
                          </div>

                          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                            <div>
                              <p className="text-gray-500">Order ID</p>
                              <p className="font-semibold">{order.order_id || order.pledge_id}</p>
                            </div>
                            <div>
                              <p className="text-gray-500">Customer</p>
                              <p className="font-semibold">{order.user_email}</p>
                            </div>
                            <div>
                              <p className="text-gray-500">Product</p>
                              <p className="font-semibold">{order.product_name}</p>
                            </div>
                            <div>
                              <p className="text-gray-500">Quantity</p>
                              <p className="font-semibold">{order.sqft?.toLocaleString()} sqft</p>
                            </div>
                          </div>

                          <div className="mt-3 flex items-center gap-6">
                            <div>
                              <span className="text-gray-500 text-sm">Amount Due: </span>
                              <span className="text-xl font-bold text-primary">
                                ${order.amount_due?.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                              </span>
                            </div>
                            <div>
                              <span className="text-gray-500 text-sm">Payment Type: </span>
                              <span className="font-semibold">
                                {order.payment_amount_type === 'full' ? '100% Full Payment' : '30% Deposit'}
                              </span>
                            </div>
                          </div>
                        </div>

                        {/* Action Buttons */}
                        <div className="flex items-center gap-3">
                          <Button
                            onClick={() => handleVerifyPayment(order.pledge_id, true)}
                            disabled={verifyingPayment === order.pledge_id}
                            className="bg-green-600 hover:bg-green-700"
                            data-testid={`verify-payment-${order.pledge_id}`}
                          >
                            {verifyingPayment === order.pledge_id ? (
                              <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                            ) : (
                              <CheckCircle className="w-4 h-4 mr-2" />
                            )}
                            Verify Payment
                          </Button>
                          <Button
                            onClick={() => handleVerifyPayment(order.pledge_id, false)}
                            disabled={verifyingPayment === order.pledge_id}
                            variant="outline"
                            className="text-red-600 border-red-300 hover:bg-red-50"
                            data-testid={`cancel-order-${order.pledge_id}`}
                          >
                            <XCircle className="w-4 h-4 mr-2" />
                            Cancel
                          </Button>
                        </div>
                      </div>

                      {/* Shipping Address */}
                      {order.shipping_address && (
                        <div className="mt-4 pt-4 border-t border-gray-200">
                          <p className="text-sm text-gray-500">Shipping Address:</p>
                          <p className="text-sm">
                            {order.shipping_address.street}, {order.shipping_address.city}, {order.shipping_address.state} {order.shipping_address.zip_code}
                          </p>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* Authorizations Tab */}
        {activeTab === 'authorizations' && (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <h2 className="text-2xl font-bold">Card Authorizations</h2>
              <span className="text-gray-500">Review and approve card payments $5,000 - $15,000</span>
            </div>

            {/* Stats Cards */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="bg-amber-50 rounded-xl p-4 border border-amber-200">
                <p className="text-sm text-amber-700">Pending Approval</p>
                <p className="text-3xl font-bold text-amber-900">
                  {authorizations.filter(a => a.status === 'AUTHORIZED').length}
                </p>
              </div>
              <div className="bg-green-50 rounded-xl p-4 border border-green-200">
                <p className="text-sm text-green-700">Captured</p>
                <p className="text-3xl font-bold text-green-900">
                  {authorizations.filter(a => a.status === 'CAPTURED').length}
                </p>
              </div>
              <div className="bg-red-50 rounded-xl p-4 border border-red-200">
                <p className="text-sm text-red-700">Voided</p>
                <p className="text-3xl font-bold text-red-900">
                  {authorizations.filter(a => a.status === 'VOIDED').length}
                </p>
              </div>
              <div className="bg-gray-50 rounded-xl p-4 border border-gray-200">
                <p className="text-sm text-gray-700">Total Value (Pending)</p>
                <p className="text-3xl font-bold text-gray-900">
                  ${authorizations.filter(a => a.status === 'AUTHORIZED').reduce((sum, a) => sum + a.amount, 0).toLocaleString()}
                </p>
              </div>
            </div>

            {authorizations.filter(a => a.status === 'AUTHORIZED').length === 0 ? (
              <div className="bg-white rounded-2xl shadow p-12 text-center">
                <ShieldCheck className="w-16 h-16 text-green-500 mx-auto mb-4" />
                <h3 className="text-xl font-bold text-gray-900 mb-2">No Pending Authorizations</h3>
                <p className="text-gray-600">All card authorizations have been processed.</p>
              </div>
            ) : (
              <div className="bg-white rounded-2xl shadow overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Customer</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Amount</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Card</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Product</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Expires</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {authorizations.filter(a => a.status === 'AUTHORIZED').map((auth) => (
                        <tr key={auth.authorization_id} className="hover:bg-gray-50">
                          <td className="px-6 py-4">
                            <div>
                              <p className="font-medium text-gray-900">{auth.email}</p>
                              <p className="text-xs text-gray-500">ID: {auth.authorization_id}</p>
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <span className="text-lg font-bold text-primary">
                              ${auth.amount.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                            </span>
                          </td>
                          <td className="px-6 py-4">
                            <div className="flex items-center gap-2">
                              <CreditCard className="w-4 h-4 text-gray-400" />
                              <span className="text-sm">
                                {auth.square_response?.card_details?.brand || 'Card'} •••• {auth.square_response?.card_details?.last_4 || '****'}
                              </span>
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <span className="text-sm text-gray-600">
                              {auth.order_data?.product_name || 'Flooring Order'}
                            </span>
                          </td>
                          <td className="px-6 py-4">
                            <span className={`text-sm ${new Date(auth.expires_at) < new Date(Date.now() + 2*24*60*60*1000) ? 'text-red-600 font-semibold' : 'text-gray-600'}`}>
                              {new Date(auth.expires_at).toLocaleDateString()}
                            </span>
                          </td>
                          <td className="px-6 py-4">
                            <div className="flex items-center gap-2">
                              <Button
                                onClick={async () => {
                                  if (processingAuth) return;
                                  setProcessingAuth(auth.authorization_id);
                                  try {
                                    await axios.post(`${BACKEND_URL}/api/admin/payments/capture-authorization/${auth.authorization_id}`, {
                                      admin_password: ADMIN_PASSWORD,
                                      admin_notes: 'Approved via Admin Panel'
                                    });
                                    toast.success('Payment captured successfully');
                                    fetchAllData();
                                  } catch (error) {
                                    toast.error(error.response?.data?.detail || 'Failed to capture payment');
                                  } finally {
                                    setProcessingAuth(null);
                                  }
                                }}
                                disabled={processingAuth === auth.authorization_id}
                                className="bg-green-600 hover:bg-green-700 text-white text-sm px-3 py-1"
                                data-testid={`capture-auth-${auth.authorization_id}`}
                              >
                                {processingAuth === auth.authorization_id ? (
                                  <RefreshCw className="w-4 h-4 animate-spin" />
                                ) : (
                                  <>
                                    <Check className="w-4 h-4 mr-1" />
                                    Capture
                                  </>
                                )}
                              </Button>
                              <Button
                                onClick={async () => {
                                  if (processingAuth) return;
                                  const reason = prompt('Enter reason for voiding (optional):');
                                  if (reason === null) return; // User cancelled
                                  setProcessingAuth(auth.authorization_id);
                                  try {
                                    await axios.post(`${BACKEND_URL}/api/admin/payments/void-authorization/${auth.authorization_id}`, {
                                      admin_password: ADMIN_PASSWORD,
                                      admin_notes: reason || 'Voided via Admin Panel'
                                    });
                                    toast.success('Authorization voided');
                                    fetchAllData();
                                  } catch (error) {
                                    toast.error(error.response?.data?.detail || 'Failed to void authorization');
                                  } finally {
                                    setProcessingAuth(null);
                                  }
                                }}
                                disabled={processingAuth === auth.authorization_id}
                                variant="outline"
                                className="border-red-300 text-red-600 hover:bg-red-50 text-sm px-3 py-1"
                                data-testid={`void-auth-${auth.authorization_id}`}
                              >
                                <X className="w-4 h-4 mr-1" />
                                Void
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* History Section */}
            {authorizations.filter(a => a.status !== 'AUTHORIZED').length > 0 && (
              <div className="mt-8">
                <h3 className="text-lg font-bold mb-4">History</h3>
                <div className="bg-white rounded-2xl shadow overflow-hidden">
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Customer</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Amount</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Notes</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200">
                        {authorizations.filter(a => a.status !== 'AUTHORIZED').slice(0, 20).map((auth) => (
                          <tr key={auth.authorization_id} className="hover:bg-gray-50">
                            <td className="px-6 py-4">
                              <p className="text-sm text-gray-900">{auth.email}</p>
                            </td>
                            <td className="px-6 py-4">
                              <span className="font-medium">
                                ${auth.amount.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                              </span>
                            </td>
                            <td className="px-6 py-4">
                              <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                                auth.status === 'CAPTURED' ? 'bg-green-100 text-green-800' :
                                auth.status === 'VOIDED' ? 'bg-red-100 text-red-800' :
                                'bg-gray-100 text-gray-800'
                              }`}>
                                {auth.status}
                              </span>
                            </td>
                            <td className="px-6 py-4">
                              <span className="text-sm text-gray-600">
                                {auth.captured_at ? new Date(auth.captured_at).toLocaleDateString() :
                                 auth.voided_at ? new Date(auth.voided_at).toLocaleDateString() :
                                 new Date(auth.created_at).toLocaleDateString()}
                              </span>
                            </td>
                            <td className="px-6 py-4">
                              <span className="text-sm text-gray-500">
                                {auth.admin_notes || '-'}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Tax Exempt Tab */}
        {activeTab === 'tax_exempt' && (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <h2 className="text-2xl font-bold">Tax Exempt Verification</h2>
              <span className="text-gray-500">Review uploaded tax exemption certificates</span>
            </div>

            {taxExemptPending.length === 0 ? (
              <div className="bg-white rounded-2xl shadow p-12 text-center">
                <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
                <h3 className="text-xl font-bold text-gray-900 mb-2">All Clear!</h3>
                <p className="text-gray-600">No pending tax exempt documents to review.</p>
              </div>
            ) : (
              <div className="space-y-4">
                {taxExemptPending.map((order) => {
                  const deadline = order.tax_exempt_deadline ? new Date(order.tax_exempt_deadline) : null;
                  const now = new Date();
                  const hoursLeft = deadline ? Math.max(0, Math.floor((deadline - now) / (1000 * 60 * 60))) : null;
                  const hasDoc = order.tax_exempt_doc_uploaded;

                  return (
                    <div
                      key={order.pledge_id}
                      className={`bg-white rounded-2xl shadow p-6 border-2 ${
                        hasDoc ? 'border-blue-300' : 'border-amber-300'
                      }`}
                      data-testid={`tax-exempt-order-${order.pledge_id}`}
                    >
                      <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-3">
                            <FileText className="w-6 h-6 text-blue-600" />
                            <span className="font-bold text-lg">Tax Exempt Review</span>
                            {hasDoc ? (
                              <span className="px-2 py-0.5 rounded-full text-xs font-bold bg-blue-100 text-blue-800">
                                Doc Uploaded
                              </span>
                            ) : (
                              <span className="px-2 py-0.5 rounded-full text-xs font-bold bg-amber-100 text-amber-800">
                                Awaiting Upload
                              </span>
                            )}
                            {hoursLeft !== null && (
                              <span className={`px-2 py-0.5 rounded-full text-xs font-bold ${
                                hoursLeft <= 0 ? 'bg-red-200 text-red-800' :
                                hoursLeft <= 24 ? 'bg-amber-200 text-amber-800' :
                                'bg-gray-100 text-gray-700'
                              }`}>
                                {hoursLeft <= 0 ? 'EXPIRED' : `${hoursLeft}h left`}
                              </span>
                            )}
                          </div>

                          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm mb-3">
                            <div>
                              <p className="text-gray-500">Order ID</p>
                              <p className="font-semibold">{order.order_id || order.pledge_id}</p>
                            </div>
                            <div>
                              <p className="text-gray-500">Customer</p>
                              <p className="font-semibold">{order.user_email}</p>
                            </div>
                            <div>
                              <p className="text-gray-500">Product</p>
                              <p className="font-semibold">{order.product_name}</p>
                            </div>
                            <div>
                              <p className="text-gray-500">Amount</p>
                              <p className="font-semibold">${(order.total_price || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}</p>
                            </div>
                          </div>

                          <div className="flex items-center gap-4 text-sm">
                            <div>
                              <span className="text-gray-500">State: </span>
                              <span className="font-semibold">{order.shipping_address?.state || 'N/A'}</span>
                            </div>
                            <div>
                              <span className="text-gray-500">Status: </span>
                              <span className="font-semibold capitalize">{(order.tax_exempt_status || 'pending').replace(/_/g, ' ')}</span>
                            </div>
                          </div>

                          {/* Document Link */}
                          {hasDoc && order.tax_exempt_doc_filename && (
                            <div className="mt-3 p-3 bg-blue-50 rounded-lg flex items-center gap-3">
                              <FileText className="w-5 h-5 text-blue-600" />
                              <span className="text-sm font-medium text-blue-800">{order.tax_exempt_doc_filename}</span>
                              <a
                                href={`${BACKEND_URL}/api/admin/tax-exempt-docs/${order.tax_exempt_doc_filename}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="ml-auto inline-flex items-center gap-1 px-3 py-1 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 transition-colors"
                                data-testid={`view-doc-${order.pledge_id}`}
                              >
                                <ExternalLink className="w-3 h-3" />
                                View Document
                              </a>
                            </div>
                          )}
                        </div>

                        {/* Action Buttons */}
                        <div className="flex items-center gap-3">
                          <Button
                            onClick={() => handleVerifyTaxExempt(order.pledge_id, true)}
                            disabled={verifyingTaxExempt === order.pledge_id || !hasDoc}
                            className="bg-green-600 hover:bg-green-700"
                            data-testid={`approve-tax-exempt-${order.pledge_id}`}
                          >
                            {verifyingTaxExempt === order.pledge_id ? (
                              <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                            ) : (
                              <CheckCircle className="w-4 h-4 mr-2" />
                            )}
                            Approve
                          </Button>
                          <Button
                            onClick={() => handleVerifyTaxExempt(order.pledge_id, false)}
                            disabled={verifyingTaxExempt === order.pledge_id}
                            variant="outline"
                            className="text-red-600 border-red-300 hover:bg-red-50"
                            data-testid={`reject-tax-exempt-${order.pledge_id}`}
                          >
                            <XCircle className="w-4 h-4 mr-2" />
                            Reject
                          </Button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* Dashboard Tab */}
        {activeTab === 'dashboard' && (
          <div className="space-y-6">
            <h2 className="text-2xl font-bold">Dashboard Overview</h2>
            
            {stats && (
              <>
                {/* Container Status Alert */}
                {stats.fill_percentage >= 75 && (
                  <div className="bg-green-100 border-2 border-green-500 rounded-2xl p-6">
                    <div className="flex items-center">
                      <div className="w-12 h-12 bg-green-500 rounded-full flex items-center justify-center mr-4">
                        <Check className="w-6 h-6 text-white" />
                      </div>
                      <div>
                        <h3 className="text-xl font-bold text-green-800">Production Ready!</h3>
                        <p className="text-green-700">Container has reached {stats.fill_percentage}% capacity. Ready to trigger production.</p>
                      </div>
                    </div>
                  </div>
                )}
                {stats.fill_percentage >= 50 && stats.fill_percentage < 75 && (
                  <div className="bg-yellow-100 border-2 border-yellow-500 rounded-2xl p-6">
                    <div className="flex items-center">
                      <div className="w-12 h-12 bg-yellow-500 rounded-full flex items-center justify-center mr-4">
                        <Ship className="w-6 h-6 text-white" />
                      </div>
                      <div>
                        <h3 className="text-xl font-bold text-yellow-800">Container Filling Up</h3>
                        <p className="text-yellow-700">Container is at {stats.fill_percentage}%. Production triggers at 80%.</p>
                      </div>
                    </div>
                  </div>
                )}

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                  <div className="bg-white rounded-2xl p-6 shadow-sm">
                    <div className="flex items-center justify-between mb-4">
                      <DollarSign className="w-8 h-8 text-green-600" />
                      <span className="text-xs text-gray-500">Total Revenue</span>
                    </div>
                    <p className="text-3xl font-bold text-gray-900">${stats.totalRevenue?.toFixed(2) || '0.00'}</p>
                  </div>

                  <div className="bg-white rounded-2xl p-6 shadow-sm">
                    <div className="flex items-center justify-between mb-4">
                      <Package className="w-8 h-8 text-blue-600" />
                      <span className="text-xs text-gray-500">Total Pledges</span>
                    </div>
                    <p className="text-3xl font-bold text-gray-900">{stats.totalPledges || 0}</p>
                  </div>

                  <div className="bg-white rounded-2xl p-6 shadow-sm">
                    <div className="flex items-center justify-between mb-4">
                      <Tag className="w-8 h-8 text-purple-600" />
                      <span className="text-xs text-gray-500">Active Coupons</span>
                    </div>
                    <p className="text-3xl font-bold text-gray-900">{stats.activeCoupons || 0}</p>
                  </div>

                  <div className={`rounded-2xl p-6 shadow-sm ${
                    stats.fill_percentage >= 75 ? 'bg-green-50 border-2 border-green-300' :
                    stats.fill_percentage >= 50 ? 'bg-yellow-50 border-2 border-yellow-300' :
                    'bg-white'
                  }`}>
                    <div className="flex items-center justify-between mb-4">
                      <Ship className={`w-8 h-8 ${
                        stats.fill_percentage >= 75 ? 'text-green-600' :
                        stats.fill_percentage >= 50 ? 'text-yellow-600' :
                        'text-orange-600'
                      }`} />
                      <span className="text-xs text-gray-500">Container Fill</span>
                    </div>
                    <p className={`text-3xl font-bold ${
                      stats.fill_percentage >= 75 ? 'text-green-700' :
                      stats.fill_percentage >= 50 ? 'text-yellow-700' :
                      'text-gray-900'
                    }`}>{stats.fill_percentage || 0}%</p>
                    <div className="mt-2 h-3 bg-gray-200 rounded-full overflow-hidden">
                      <div 
                        className={`h-full rounded-full transition-all ${
                          stats.fill_percentage >= 75 ? 'bg-green-500' :
                          stats.fill_percentage >= 50 ? 'bg-yellow-500' :
                          'bg-orange-500'
                        }`}
                        style={{ width: `${stats.fill_percentage || 0}%` }}
                      />
                    </div>
                    <p className="text-xs text-gray-500 mt-1">
                      {(stats.containerFill || 0).toLocaleString()} / {(stats.containerCapacity || 26900).toLocaleString()} sqft
                    </p>
                    {stats.container_number && (
                      <p className="text-xs font-semibold text-primary mt-2">
                        Container #{stats.container_number}
                      </p>
                    )}
                  </div>
                </div>

                {/* Export & Business Stats */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="bg-white rounded-2xl p-6 shadow-sm">
                    <h3 className="font-bold text-lg mb-4">Warehouse Export</h3>
                    <p className="text-sm text-gray-600 mb-4">
                      Download all pledge details as CSV for your warehouse team.
                    </p>
                    <Button
                      onClick={() => window.open(`${BACKEND_URL}/api/admin/export/pledges`, '_blank')}
                      className="w-full"
                      disabled={stats.totalPledges === 0}
                    >
                      <Package className="w-4 h-4 mr-2" />
                      Export Pledges to CSV
                    </Button>
                  </div>

                  <div className="bg-white rounded-2xl p-6 shadow-sm">
                    <h3 className="font-bold text-lg mb-4">Business Metrics</h3>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-gray-600">Your Cost:</span>
                        <span className="font-semibold">${stats.business_cost_per_sqft || 0.96}/sqft</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Total Sqft Sold:</span>
                        <span className="font-semibold">{(stats.total_sqft_sold || 0).toLocaleString()}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Total Cost:</span>
                        <span className="font-semibold">${(stats.total_cost || 0).toFixed(2)}</span>
                      </div>
                      <div className="flex justify-between pt-2 border-t">
                        <span className="text-gray-600 font-semibold">Est. Profit:</span>
                        <span className="font-bold text-green-600">${(stats.estimated_profit || 0).toFixed(2)}</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Danger Zone - Reset Data */}
                <div className="bg-white rounded-2xl shadow-sm p-6 border-2 border-red-200">
                  <h3 className="font-bold text-lg mb-4 text-red-600 flex items-center">
                    <AlertTriangle className="w-5 h-5 mr-2" />
                    Danger Zone
                  </h3>
                  <p className="text-gray-600 text-sm mb-4">
                    Reset all transactional data including pledges, containers, and transactions. 
                    This action cannot be undone.
                  </p>
                  <Button 
                    onClick={() => setShowResetConfirm(true)}
                    variant="outline"
                    className="border-red-500 text-red-600 hover:bg-red-50"
                    data-testid="reset-data-btn"
                  >
                    <Trash2 className="w-4 h-4 mr-2" />
                    Reset All Data
                  </Button>
                </div>
              </>
            )}
          </div>
        )}

        {/* Reset Confirmation Modal */}
        {showResetConfirm && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6">
              <div className="text-center mb-6">
                <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <AlertTriangle className="w-8 h-8 text-red-600" />
                </div>
                <h3 className="text-xl font-bold text-red-600">Reset All Data?</h3>
                <p className="text-gray-600 text-sm mt-2">
                  This will permanently delete all pledges, containers, and transactions. 
                  Product catalog and coupons will be preserved.
                </p>
              </div>

              <div className="mb-6">
                <label className="block text-sm font-semibold mb-2">
                  Type <span className="text-red-600 font-mono">RESET</span> to confirm:
                </label>
                <input
                  type="text"
                  value={resetConfirmText}
                  onChange={(e) => setResetConfirmText(e.target.value)}
                  className="w-full p-3 border-2 border-gray-200 rounded-xl focus:border-red-500 focus:outline-none font-mono text-center"
                  placeholder="RESET"
                  data-testid="reset-confirm-input"
                />
              </div>

              <div className="flex gap-3">
                <Button
                  onClick={() => {
                    setShowResetConfirm(false);
                    setResetConfirmText('');
                  }}
                  variant="outline"
                  className="flex-1"
                  disabled={resetting}
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleResetAllData}
                  className="flex-1 bg-red-600 hover:bg-red-700"
                  disabled={resetting || resetConfirmText !== 'RESET'}
                  data-testid="confirm-reset-btn"
                >
                  {resetting ? (
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <>
                      <Trash2 className="w-4 h-4 mr-2" />
                      Reset Everything
                    </>
                  )}
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Members Tab */}
        {activeTab === 'members' && (
          <div className="space-y-6">
            <div className="flex justify-between items-center">
              <h2 className="text-2xl font-bold flex items-center gap-2">
                <Star className="w-6 h-6 text-amber-500" />
                Members ({members.length})
              </h2>
              <div className="flex gap-2">
                <Button 
                  variant="outline"
                  onClick={() => {
                    window.open('https://squareup.com/dashboard/customers', '_blank');
                  }}
                >
                  <ExternalLink className="w-4 h-4 mr-2" /> Square Dashboard
                </Button>
              </div>
            </div>

            {/* Members Stats */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="bg-gradient-to-br from-amber-50 to-amber-100 rounded-xl p-6">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-amber-500 rounded-full flex items-center justify-center">
                    <Star className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <p className="text-sm text-amber-700">Active Members</p>
                    <p className="text-3xl font-bold text-amber-900">
                      {members.filter(m => m.membership_status === 'active').length}
                    </p>
                  </div>
                </div>
              </div>
              <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-xl p-6">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-green-500 rounded-full flex items-center justify-center">
                    <DollarSign className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <p className="text-sm text-green-700">Membership Revenue</p>
                    <p className="text-3xl font-bold text-green-900">
                      ${(members.filter(m => m.membership_status === 'active').length * 600).toLocaleString()}
                    </p>
                  </div>
                </div>
              </div>
              <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl p-6">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-blue-500 rounded-full flex items-center justify-center">
                    <Package className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <p className="text-sm text-blue-700">Member Orders</p>
                    <p className="text-3xl font-bold text-blue-900">
                      {members.reduce((sum, m) => sum + (m.total_orders || 0), 0)}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Members Table */}
            <div className="bg-white rounded-xl shadow overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 border-b">
                    <tr>
                      <th className="px-4 py-3 text-left text-sm font-medium text-gray-500">Member</th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-gray-500">Status</th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-gray-500">Member Since</th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-gray-500">Expires</th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-gray-500">Orders</th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-gray-500">Total Spend</th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-gray-500">Source</th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-gray-500">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {members.length > 0 ? members.map((member) => (
                      <tr key={member.user_id} className="hover:bg-gray-50">
                        <td className="px-4 py-4">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center">
                              <Star className="w-5 h-5 text-amber-600" />
                            </div>
                            <div>
                              <p className="font-medium text-gray-900">{member.name || 'No name'}</p>
                              <p className="text-sm text-gray-500">{member.email}</p>
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-4">
                          <span className={`px-2 py-1 rounded-full text-xs font-bold ${
                            member.membership_status === 'active' 
                              ? 'bg-green-100 text-green-700' 
                              : member.membership_status === 'cancelled'
                              ? 'bg-red-100 text-red-700'
                              : 'bg-gray-100 text-gray-700'
                          }`}>
                            {member.membership_status?.toUpperCase() || 'UNKNOWN'}
                          </span>
                        </td>
                        <td className="px-4 py-4 text-sm text-gray-600">
                          {member.membership_started 
                            ? new Date(member.membership_started).toLocaleDateString() 
                            : '-'}
                        </td>
                        <td className="px-4 py-4 text-sm text-gray-600">
                          {member.membership_expires 
                            ? new Date(member.membership_expires).toLocaleDateString() 
                            : '-'}
                        </td>
                        <td className="px-4 py-4 text-sm font-medium text-gray-900">
                          {member.total_orders || 0}
                        </td>
                        <td className="px-4 py-4 text-sm font-medium text-gray-900">
                          ${(member.total_spend || 0).toLocaleString()}
                        </td>
                        <td className="px-4 py-4 text-sm text-gray-600">
                          <span className={`px-2 py-1 rounded text-xs ${
                            member.membership_source === 'square_payment' 
                              ? 'bg-blue-100 text-blue-700' 
                              : member.membership_source === 'admin_granted'
                              ? 'bg-purple-100 text-purple-700'
                              : 'bg-gray-100 text-gray-700'
                          }`}>
                            {member.membership_source === 'square_payment' ? 'Square' : 
                             member.membership_source === 'admin_granted' ? 'Admin' : 
                             member.membership_source || 'Manual'}
                          </span>
                        </td>
                        <td className="px-4 py-4">
                          <div className="flex items-center gap-2">
                            {member.membership_status === 'active' ? (
                              <Button
                                size="sm"
                                variant="outline"
                                className="text-red-600 hover:bg-red-50"
                                onClick={async () => {
                                  if (window.confirm(`Cancel membership for ${member.email}?`)) {
                                    try {
                                      await axios.post(`${BACKEND_URL}/api/admin/members/${member.user_id}/cancel`);
                                      toast.success('Membership cancelled');
                                      fetchAllData();
                                    } catch (e) {
                                      toast.error('Failed to cancel membership');
                                    }
                                  }
                                }}
                              >
                                Cancel
                              </Button>
                            ) : (
                              <Button
                                size="sm"
                                variant="outline"
                                className="text-green-600 hover:bg-green-50"
                                onClick={async () => {
                                  try {
                                    await axios.post(`${BACKEND_URL}/api/admin/members/add`, { user_id: member.user_id });
                                    toast.success('Membership reactivated');
                                    fetchAllData();
                                  } catch (e) {
                                    toast.error('Failed to reactivate membership');
                                  }
                                }}
                              >
                                Reactivate
                              </Button>
                            )}
                          </div>
                        </td>
                      </tr>
                    )) : (
                      <tr>
                        <td colSpan="8" className="px-4 py-12 text-center">
                          <Star className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                          <p className="text-gray-500">No members yet</p>
                          <p className="text-sm text-gray-400 mt-1">Members will appear here when they subscribe</p>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Grant Membership Section */}
            <div className="bg-white rounded-xl shadow p-6">
              <h3 className="text-lg font-bold mb-4">Grant Membership to Existing User</h3>
              <p className="text-sm text-gray-600 mb-4">Select a user from the list below to grant them a 1-year membership.</p>
              <div className="flex gap-4">
                <select 
                  className="flex-1 border border-gray-300 rounded-lg px-4 py-2"
                  id="grant-member-select"
                  defaultValue=""
                >
                  <option value="">Select a user...</option>
                  {users.filter(u => !members.find(m => m.user_id === u.user_id)).map(user => (
                    <option key={user.user_id} value={user.user_id}>
                      {user.email} ({user.name || 'No name'})
                    </option>
                  ))}
                </select>
                <Button
                  onClick={async () => {
                    const select = document.getElementById('grant-member-select');
                    const userId = select.value;
                    if (!userId) {
                      toast.error('Please select a user');
                      return;
                    }
                    try {
                      await axios.post(`${BACKEND_URL}/api/admin/members/add`, { user_id: userId });
                      toast.success('Membership granted successfully!');
                      fetchAllData();
                      select.value = '';
                    } catch (e) {
                      toast.error('Failed to grant membership');
                    }
                  }}
                >
                  <Star className="w-4 h-4 mr-2" /> Grant Membership
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Users Tab */}
        {activeTab === 'users' && (
          <div className="space-y-6">
            <div className="flex justify-between items-center">
              <h2 className="text-2xl font-bold">User Management ({users.length})</h2>
              <Button onClick={() => setShowCreateUser(true)} data-testid="create-user-btn">
                <Plus className="w-4 h-4 mr-2" /> Create Account
              </Button>
            </div>

            <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-sm font-medium text-gray-500">User</th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-gray-500">Email</th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-gray-500">Membership</th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-gray-500">Orders</th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-gray-500">Total Spent</th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-gray-500">Joined</th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-gray-500">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {users.map((user, idx) => (
                      <tr key={idx} className="hover:bg-gray-50">
                        <td className="px-4 py-3">
                          <div className="flex items-center space-x-3">
                            {user.picture ? (
                              <img src={user.picture} alt="" className="w-8 h-8 rounded-full" />
                            ) : (
                              <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                                <Users className="w-4 h-4 text-primary" />
                              </div>
                            )}
                            <span className="font-medium">{user.name || 'Guest'}</span>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center space-x-2">
                            <Mail className="w-4 h-4 text-gray-400" />
                            <span className="text-sm">{user.email}</span>
                            <button 
                              onClick={() => copyToClipboard(user.email)}
                              className="text-gray-400 hover:text-gray-600"
                            >
                              <Copy className="w-3 h-3" />
                            </button>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium inline-flex items-center ${
                            user.membership_tier === 'elite' ? 'bg-purple-100 text-purple-800' :
                            user.membership_tier === 'pro' ? 'bg-blue-100 text-blue-800' :
                            user.membership_tier === 'basic' ? 'bg-green-100 text-green-800' :
                            'bg-gray-100 text-gray-800'
                          }`}>
                            {user.membership_tier === 'elite' && <Crown className="w-3 h-3 mr-1" />}
                            {(user.membership_tier || 'non-member').toUpperCase()}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-sm">{user.order_count || 0}</td>
                        <td className="px-4 py-3 text-sm font-medium">${(user.total_spent || 0).toFixed(2)}</td>
                        <td className="px-4 py-3 text-sm text-gray-500">
                          {user.created_at ? new Date(user.created_at).toLocaleDateString() : '-'}
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => setEditingUser(user)}
                              className="px-3 py-1 bg-gray-100 hover:bg-gray-200 text-gray-700 text-sm rounded-lg inline-flex items-center"
                            >
                              <Edit2 className="w-3 h-3 mr-1" />
                              Edit
                            </button>
                            <button
                              onClick={() => setDeletingUser(user)}
                              className="px-3 py-1 bg-red-50 hover:bg-red-100 text-red-600 text-sm rounded-lg inline-flex items-center"
                              data-testid={`delete-user-${user.user_id}`}
                            >
                              <Trash2 className="w-3 h-3 mr-1" />
                              Delete
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                    {users.length === 0 && (
                      <tr>
                        <td colSpan="7" className="px-4 py-12 text-center text-gray-500">
                          <Users className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                          <p className="text-lg font-medium">No users yet</p>
                          <p className="text-sm">Users will appear here once they sign up or place orders.</p>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Edit User Modal */}
            {editingUser && (
              <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
                <div className="bg-white rounded-2xl p-6 w-full max-w-md shadow-2xl">
                  <h3 className="text-xl font-bold mb-4">Edit User</h3>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium mb-1">Email</label>
                      <input
                        type="text"
                        value={editingUser.email}
                        disabled
                        className="w-full p-2 border rounded-lg bg-gray-50 text-gray-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">Name</label>
                      <input
                        type="text"
                        value={editingUser.name || ''}
                        onChange={(e) => setEditingUser({ ...editingUser, name: e.target.value })}
                        className="w-full p-2 border rounded-lg"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">Membership Tier</label>
                      <select
                        value={editingUser.membership_tier || 'basic'}
                        onChange={(e) => setEditingUser({ ...editingUser, membership_tier: e.target.value })}
                        className="w-full p-2 border rounded-lg"
                      >
                        <option value="non-member">Non-Member</option>
                        <option value="basic">Basic</option>
                        <option value="pro">Pro</option>
                        <option value="elite">Elite</option>
                      </select>
                    </div>
                  </div>
                  <div className="flex gap-3 mt-6">
                    <Button
                      onClick={async () => {
                        try {
                          await axios.patch(`${BACKEND_URL}/api/admin/users/${editingUser.user_id}`, {
                            name: editingUser.name,
                            membership_tier: editingUser.membership_tier
                          });
                          toast.success('User updated');
                          setUsers(users.map(u => u.user_id === editingUser.user_id ? editingUser : u));
                          setEditingUser(null);
                        } catch (error) {
                          toast.error('Failed to update user');
                        }
                      }}
                      className="flex-1"
                    >
                      Save Changes
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => setEditingUser(null)}
                      className="flex-1"
                    >
                      Cancel
                    </Button>
                  </div>
                </div>
              </div>
            )}

            {/* Delete User Confirmation Modal */}
            {deletingUser && (
              <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                <div className="bg-white rounded-2xl p-6 w-full max-w-sm shadow-2xl">
                  <div className="text-center mb-4">
                    <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-3">
                      <AlertTriangle className="w-6 h-6 text-red-600" />
                    </div>
                    <h3 className="text-lg font-bold">Delete Account?</h3>
                    <p className="text-sm text-gray-500 mt-1">
                      This will permanently delete <strong>{deletingUser.name || deletingUser.email}</strong>'s account. Their orders will be kept for records.
                    </p>
                  </div>
                  <div className="flex gap-3">
                    <Button
                      onClick={async () => {
                        try {
                          await axios.delete(`${BACKEND_URL}/api/admin/users/${deletingUser.user_id}`);
                          toast.success(`Account ${deletingUser.email} deleted`);
                          setUsers(users.filter(u => u.user_id !== deletingUser.user_id));
                          setDeletingUser(null);
                        } catch (error) {
                          toast.error(error.response?.data?.detail || 'Failed to delete user');
                        }
                      }}
                      className="flex-1 bg-red-600 hover:bg-red-700"
                      data-testid="confirm-delete-user"
                    >
                      <Trash2 className="w-4 h-4 mr-2" /> Delete
                    </Button>
                    <Button variant="outline" onClick={() => setDeletingUser(null)} className="flex-1">Cancel</Button>
                  </div>
                </div>
              </div>
            )}

            {/* Create User Modal */}
            {showCreateUser && (
              <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                <div className="bg-white rounded-2xl p-6 w-full max-w-lg shadow-2xl max-h-[90vh] overflow-y-auto">
                  {createdUserCreds ? (
                    <>
                      <div className="text-center mb-4">
                        <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-2" />
                        <h3 className="text-xl font-bold">Account Created</h3>
                        <p className="text-sm text-gray-500 mt-1">A welcome email has been sent with these credentials</p>
                      </div>
                      <div className="bg-blue-50 border-2 border-blue-200 rounded-xl p-4 text-center space-y-3" data-testid="created-user-creds">
                        <div>
                          <p className="text-xs text-gray-500">Email</p>
                          <p className="font-semibold">{createdUserCreds.email}</p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-500">Temporary Password</p>
                          <p className="font-bold text-xl tracking-widest text-blue-700">{createdUserCreds.temp_password}</p>
                        </div>
                        <button onClick={() => { navigator.clipboard.writeText(createdUserCreds.temp_password); toast.success('Password copied!'); }}
                          className="text-sm text-blue-600 hover:underline inline-flex items-center gap-1">
                          <Copy className="w-3 h-3" /> Copy Password
                        </button>
                      </div>
                      <Button onClick={resetCreateUserForm} className="w-full mt-4" data-testid="create-user-done">Done</Button>
                    </>
                  ) : (
                    <>
                      <h3 className="text-xl font-bold mb-4">Create Customer Account</h3>
                      <div className="space-y-3">
                        <div>
                          <label className="block text-sm font-medium mb-1">Email <span className="text-red-500">*</span></label>
                          <input type="email" value={createUserForm.email}
                            onChange={e => setCreateUserForm(f => ({ ...f, email: e.target.value }))}
                            className="w-full p-2.5 border-2 border-gray-200 rounded-xl focus:border-primary focus:outline-none"
                            placeholder="customer@email.com" data-testid="create-user-email" />
                        </div>
                        <div>
                          <label className="block text-sm font-medium mb-1">Full Name</label>
                          <input type="text" value={createUserForm.name}
                            onChange={e => setCreateUserForm(f => ({ ...f, name: e.target.value }))}
                            className="w-full p-2.5 border-2 border-gray-200 rounded-xl focus:border-primary focus:outline-none"
                            placeholder="John Doe" data-testid="create-user-name" />
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <label className="block text-sm font-medium mb-1">Phone</label>
                            <input type="tel" value={createUserForm.phone}
                              onChange={e => setCreateUserForm(f => ({ ...f, phone: e.target.value }))}
                              className="w-full p-2.5 border-2 border-gray-200 rounded-xl focus:border-primary focus:outline-none"
                              placeholder="(555) 123-4567" data-testid="create-user-phone" />
                          </div>
                          <div>
                            <label className="block text-sm font-medium mb-1">Membership Tier</label>
                            <select value={createUserForm.membership_tier}
                              onChange={e => setCreateUserForm(f => ({ ...f, membership_tier: e.target.value }))}
                              className="w-full p-2.5 border-2 border-gray-200 rounded-xl focus:border-primary focus:outline-none"
                              data-testid="create-user-tier">
                              <option value="none">Non-Member</option>
                              <option value="basic">Basic</option>
                              <option value="pro">Pro</option>
                              <option value="elite">Elite</option>
                            </select>
                          </div>
                        </div>
                        <div className="pt-2">
                          <p className="text-sm font-medium mb-2 text-gray-600">Shipping Address (optional)</p>
                          <div className="space-y-2">
                            <input type="text" value={createUserForm.address.street}
                              onChange={e => setCreateUserForm(f => ({ ...f, address: { ...f.address, street: e.target.value } }))}
                              className="w-full p-2.5 border-2 border-gray-200 rounded-xl focus:border-primary focus:outline-none"
                              placeholder="Street address" />
                            <div className="grid grid-cols-3 gap-2">
                              <input type="text" value={createUserForm.address.city}
                                onChange={e => setCreateUserForm(f => ({ ...f, address: { ...f.address, city: e.target.value } }))}
                                className="w-full p-2.5 border-2 border-gray-200 rounded-xl focus:border-primary focus:outline-none"
                                placeholder="City" />
                              <input type="text" value={createUserForm.address.state}
                                onChange={e => setCreateUserForm(f => ({ ...f, address: { ...f.address, state: e.target.value } }))}
                                className="w-full p-2.5 border-2 border-gray-200 rounded-xl focus:border-primary focus:outline-none"
                                placeholder="State" />
                              <input type="text" value={createUserForm.address.zip}
                                onChange={e => setCreateUserForm(f => ({ ...f, address: { ...f.address, zip: e.target.value } }))}
                                className="w-full p-2.5 border-2 border-gray-200 rounded-xl focus:border-primary focus:outline-none"
                                placeholder="ZIP" />
                            </div>
                          </div>
                        </div>
                      </div>
                      <p className="text-xs text-gray-400 mt-3">A temporary password will be generated and emailed to the customer.</p>
                      <div className="flex gap-3 mt-4">
                        <Button onClick={handleCreateUser} disabled={creatingUser} className="flex-1" data-testid="create-user-submit">
                          {creatingUser ? <><RefreshCw className="w-4 h-4 mr-2 animate-spin" /> Creating...</> : <><Plus className="w-4 h-4 mr-2" /> Create Account</>}
                        </Button>
                        <Button variant="outline" onClick={resetCreateUserForm} className="flex-1">Cancel</Button>
                      </div>
                    </>
                  )}
                </div>
              </div>
            )}
          </div>
        )}
        {activeTab === 'coupons' && (
          <div className="space-y-6">
            <div className="flex justify-between items-center">
              <h2 className="text-2xl font-bold">Coupon Management</h2>
            </div>

            {/* Create New Coupon */}
            <div className="bg-white rounded-2xl p-6 shadow-sm">
              <h3 className="text-lg font-semibold mb-4">Create New Coupon</h3>
              <form onSubmit={createCoupon} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Coupon Code</label>
                  <input
                    type="text"
                    value={newCoupon.code}
                    onChange={(e) => setNewCoupon({ ...newCoupon, code: e.target.value.toUpperCase() })}
                    className="w-full p-2 border rounded-lg"
                    placeholder="e.g., SUMMER2026"
                    data-testid="coupon-code-input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Membership Tier</label>
                  <select
                    value={newCoupon.membership_tier}
                    onChange={(e) => setNewCoupon({ ...newCoupon, membership_tier: e.target.value })}
                    className="w-full p-2 border rounded-lg"
                    data-testid="coupon-tier-select"
                  >
                    <option value="basic">Basic</option>
                    <option value="pro">Pro</option>
                    <option value="elite">Elite</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Usage Type</label>
                  <select
                    value={newCoupon.is_multi_use ? 'multi' : 'single'}
                    onChange={(e) => setNewCoupon({ 
                      ...newCoupon, 
                      is_multi_use: e.target.value === 'multi',
                      usage_limit: e.target.value === 'multi' ? null : 1
                    })}
                    className="w-full p-2 border rounded-lg"
                    data-testid="coupon-usage-select"
                  >
                    <option value="multi">Multi-use (unlimited)</option>
                    <option value="single">Single-use</option>
                  </select>
                </div>
                <div className="flex items-end">
                  <Button type="submit" className="w-full" data-testid="create-coupon-btn">
                    <Plus className="w-4 h-4 mr-2" />
                    Create Coupon
                  </Button>
                </div>
              </form>
            </div>

            {/* Existing Coupons */}
            <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
              <div className="p-4 border-b">
                <h3 className="text-lg font-semibold">Existing Coupons</h3>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-sm font-medium text-gray-500">Code</th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-gray-500">Tier</th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-gray-500">Type</th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-gray-500">Usage</th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-gray-500">Status</th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-gray-500">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {coupons.map((coupon, idx) => (
                      <tr key={idx} className="hover:bg-gray-50">
                        <td className="px-4 py-3">
                          <div className="flex items-center space-x-2">
                            <span className="font-mono font-semibold">{coupon.code}</span>
                            <button 
                              onClick={() => copyToClipboard(coupon.code)}
                              className="text-gray-400 hover:text-gray-600"
                            >
                              <Copy className="w-4 h-4" />
                            </button>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                            coupon.membership_tier === 'elite' ? 'bg-purple-100 text-purple-800' :
                            coupon.membership_tier === 'pro' ? 'bg-blue-100 text-blue-800' :
                            'bg-gray-100 text-gray-800'
                          }`}>
                            {coupon.membership_tier?.toUpperCase()}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-600">
                          {coupon.is_multi_use ? 'Multi-use' : 'Single-use'}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-600">
                          {coupon.usage_count || 0} uses
                        </td>
                        <td className="px-4 py-3">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                            coupon.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                          }`}>
                            {coupon.is_active ? 'Active' : 'Inactive'}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <button
                            onClick={() => toggleCouponStatus(coupon.code, coupon.is_active)}
                            className={`px-3 py-1 rounded-lg text-sm font-medium ${
                              coupon.is_active 
                                ? 'bg-red-100 text-red-700 hover:bg-red-200' 
                                : 'bg-green-100 text-green-700 hover:bg-green-200'
                            }`}
                          >
                            {coupon.is_active ? 'Deactivate' : 'Activate'}
                          </button>
                        </td>
                      </tr>
                    ))}
                    {coupons.length === 0 && (
                      <tr>
                        <td colSpan="6" className="px-4 py-8 text-center text-gray-500">
                          No coupons found. Create one above.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* Pledges Tab */}
        {activeTab === 'pledges' && (
          <div className="space-y-6">
            <div className="flex justify-between items-center">
              <h2 className="text-2xl font-bold">Pledges ({pledges.length})</h2>
              <Button onClick={() => setShowCreateOrder(true)} data-testid="create-order-btn">
                <Plus className="w-4 h-4 mr-2" /> Assign Order
              </Button>
            </div>
            <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-sm font-medium text-gray-500">ID</th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-gray-500">Customer</th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-gray-500">Product</th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-gray-500">Sqft</th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-gray-500">Boxes</th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-gray-500">Total</th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-gray-500">Paid</th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-gray-500">Status</th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-gray-500">Date</th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-gray-500">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {pledges.map((pledge, idx) => (
                      <tr key={idx} className="hover:bg-gray-50">
                        <td className="px-4 py-3 font-mono text-sm">{pledge.pledge_id?.slice(-8)}</td>
                        <td className="px-4 py-3 text-sm">{pledge.user_email}</td>
                        <td className="px-4 py-3 text-sm">{pledge.product_name}</td>
                        <td className="px-4 py-3 text-sm" data-testid={`pledge-sqft-${pledge.pledge_id}`}>
                          {(() => {
                            const li = pledge.line_items || [];
                            const sqftTotal = li.filter(x => (x.unit || 'sqft') !== 'piece').reduce((a, b) => a + (parseFloat(b.sqft || b.quantity || 0)), 0);
                            const pieceTotal = li.filter(x => (x.unit || 'sqft') === 'piece').reduce((a, b) => a + (parseFloat(b.quantity || b.sqft || 0)), 0);
                            const parts = [];
                            if (sqftTotal > 0) parts.push(`${sqftTotal.toLocaleString()} sqft`);
                            if (pieceTotal > 0) parts.push(`${pieceTotal} pcs`);
                            if (parts.length) return parts.join(' + ');
                            return pledge.sqft?.toLocaleString() || '-';
                          })()}
                        </td>
                        <td className="px-4 py-3 text-sm" data-testid={`pledge-boxes-${pledge.pledge_id}`}>
                          {(() => {
                            const li = pledge.line_items || [];
                            const hasPieceOnly = li.length > 0 && li.every(x => (x.unit || 'sqft') === 'piece');
                            if (hasPieceOnly) return <span className="text-gray-400">n/a</span>;
                            if (pledge.boxes_needed) return <span className="font-medium text-primary">{pledge.boxes_needed}</span>;
                            if (pledge.sqft && pledge.sqft_per_box) return <span className="font-medium text-primary">{Math.ceil(pledge.sqft / pledge.sqft_per_box)}</span>;
                            return <span className="text-gray-400">-</span>;
                          })()}
                        </td>
                        <td className="px-4 py-3 text-sm font-medium">${parseFloat(pledge.total_price || 0).toFixed(2)}</td>
                        <td className="px-4 py-3 text-sm font-medium text-green-600">${parseFloat(pledge.deposit_paid || 0).toFixed(2)}</td>
                        <td className="px-4 py-3">
                          {/* Order Workflow Status Badge */}
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                            pledge.order_status === 'production_complete' ? 'bg-orange-100 text-orange-800' :
                            pledge.order_status === 'in_production' ? 'bg-purple-100 text-purple-800' :
                            pledge.order_status === 'po_sent' ? 'bg-blue-100 text-blue-800' :
                            pledge.order_status === 'at_port' ? 'bg-cyan-100 text-cyan-800' :
                            pledge.order_status === 'on_vessel' ? 'bg-indigo-100 text-indigo-800' :
                            pledge.order_status === 'arrived' ? 'bg-teal-100 text-teal-800' :
                            pledge.order_status === 'unloaded' ? 'bg-sky-100 text-sky-800' :
                            pledge.order_status === 'customs_cleared' ? 'bg-emerald-100 text-emerald-800' :
                            pledge.order_status === 'on_rail' ? 'bg-blue-100 text-blue-800' :
                            pledge.order_status === 'out_for_delivery' ? 'bg-orange-100 text-orange-800' :
                            pledge.order_status === 'delivered' ? 'bg-green-100 text-green-800' :
                            pledge.status === 'cancelled' ? 'bg-red-100 text-red-800' :
                            pledge.status === 'pending_payment' ? 'bg-amber-100 text-amber-800' :
                            'bg-gray-100 text-gray-800'
                          }`}>
                            {(pledge.order_status || pledge.status || 'filling')?.replace(/_/g, ' ')}
                          </span>
                          {/* Payment indicator */}
                          {pledge.deposit_paid >= pledge.total_price ? (
                            <span className="ml-1 px-1.5 py-0.5 rounded text-[10px] font-bold bg-green-200 text-green-800">PAID</span>
                          ) : pledge.deposit_paid > 0 ? (
                            <span className="ml-1 px-1.5 py-0.5 rounded text-[10px] font-bold bg-amber-200 text-amber-800">BAL DUE</span>
                          ) : null}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-500">
                          {pledge.created_at ? new Date(pledge.created_at).toLocaleDateString() : '-'}
                        </td>
                        <td className="px-4 py-3">
                          <div className="relative">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => setOrderActionsOpen(orderActionsOpen === pledge.pledge_id ? null : pledge.pledge_id)}
                              className="text-gray-600 hover:text-gray-800 hover:bg-gray-100"
                              data-testid={`order-actions-${pledge.pledge_id}`}
                            >
                              {sendingInvoice === pledge.pledge_id ? (
                                <RefreshCw className="w-4 h-4 animate-spin" />
                              ) : (
                                <ChevronRight className={`w-4 h-4 transition-transform ${orderActionsOpen === pledge.pledge_id ? 'rotate-90' : ''}`} />
                              )}
                            </Button>
                            
                            {orderActionsOpen === pledge.pledge_id && (
                              <div className="absolute right-0 top-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg py-1 z-50 min-w-[160px]">
                                <button
                                  onClick={() => { handleEditOrder(pledge); setOrderActionsOpen(null); }}
                                  className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-blue-50 hover:text-blue-700 flex items-center gap-2"
                                  data-testid={`edit-order-${pledge.pledge_id}`}
                                >
                                  <Edit2 className="w-4 h-4" /> Edit Order
                                </button>
                                <button
                                  onClick={() => handleDownloadInvoice(pledge)}
                                  className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-green-50 hover:text-green-700 flex items-center gap-2"
                                  data-testid={`download-invoice-${pledge.pledge_id}`}
                                >
                                  <FileDown className="w-4 h-4" /> Download Invoice
                                </button>
                                <button
                                  onClick={() => handleSendInvoice(pledge)}
                                  className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-purple-50 hover:text-purple-700 flex items-center gap-2"
                                  data-testid={`send-invoice-${pledge.pledge_id}`}
                                >
                                  <Mail className="w-4 h-4" /> Send Invoice
                                </button>
                                {pledge.order_status === 'production_complete' && pledge.deposit_paid < pledge.total_price && (
                                  <button
                                    onClick={() => handleSendReminder(pledge)}
                                    className="w-full px-4 py-2 text-left text-sm text-orange-600 hover:bg-orange-50 flex items-center gap-2"
                                    data-testid={`send-reminder-${pledge.pledge_id}`}
                                  >
                                    <AlertTriangle className="w-4 h-4" /> Send Payment Reminder
                                  </button>
                                )}
                                <hr className="my-1" />
                                <button
                                  onClick={() => { handleDeleteOrder(pledge.pledge_id); setOrderActionsOpen(null); }}
                                  disabled={deletingOrder === pledge.pledge_id}
                                  className="w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-red-50 flex items-center gap-2"
                                  data-testid={`delete-order-${pledge.pledge_id}`}
                                >
                                  {deletingOrder === pledge.pledge_id ? (
                                    <RefreshCw className="w-4 h-4 animate-spin" />
                                  ) : (
                                    <Trash2 className="w-4 h-4" />
                                  )}
                                  Delete Order
                                </button>
                              </div>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                    {pledges.length === 0 && (
                      <tr>
                        <td colSpan="9" className="px-4 py-8 text-center text-gray-500">
                          No pledges yet.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Create Order Modal */}
            {showCreateOrder && (
              <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                <div className="bg-white rounded-2xl p-6 w-full max-w-lg shadow-2xl max-h-[90vh] overflow-y-auto">
                  {createdOrder ? (
                    <>
                      <div className="text-center mb-4">
                        <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-2" />
                        <h3 className="text-xl font-bold">Order Created</h3>
                      </div>
                      <div className="bg-green-50 border border-green-200 rounded-xl p-4 space-y-2 text-sm" data-testid="created-order-details">
                        <div className="flex justify-between"><span className="text-gray-500">Customer</span><span className="font-medium">{createdOrder.email}</span></div>
                        <div className="flex justify-between"><span className="text-gray-500">Product</span><span className="font-medium">{createdOrder.product_name}</span></div>
                        <div className="flex justify-between"><span className="text-gray-500">Sqft</span><span className="font-medium">{createdOrder.sqft?.toLocaleString()}</span></div>
                        <div className="flex justify-between"><span className="text-gray-500">Total</span><span className="font-bold">${createdOrder.total_price?.toFixed(2)}</span></div>
                        <div className="flex justify-between"><span className="text-gray-500">Paid</span><span className="font-medium text-green-700">${createdOrder.amount_paid?.toFixed(2)}</span></div>
                        {createdOrder.balance_due > 0 && (
                          <div className="flex justify-between"><span className="text-gray-500">Balance Due</span><span className="font-bold text-red-600">${createdOrder.balance_due?.toFixed(2)}</span></div>
                        )}
                        <div className="flex justify-between"><span className="text-gray-500">Container</span><span className="font-medium">#{createdOrder.container_number || createdOrder.container_id}</span></div>
                        <div className="flex justify-between"><span className="text-gray-500">Order ID</span><span className="font-mono text-xs">{createdOrder.pledge_id}</span></div>
                      </div>
                      <Button onClick={resetCreateOrderForm} className="w-full mt-4" data-testid="create-order-done">Done</Button>
                    </>
                  ) : (
                    <>
                      <h3 className="text-xl font-bold mb-4">Assign Order to Customer</h3>
                      <div className="space-y-3">
                        <div>
                          <label className="block text-sm font-medium mb-1">Customer Email <span className="text-red-500">*</span></label>
                          <select
                            value={createOrderForm.email}
                            onChange={e => setCreateOrderForm(f => ({ ...f, email: e.target.value }))}
                            className="w-full p-2.5 border-2 border-gray-200 rounded-xl focus:border-primary focus:outline-none"
                            data-testid="order-customer-select"
                          >
                            <option value="">Select customer...</option>
                            {users.map(u => (
                              <option key={u.user_id} value={u.email}>{u.name || u.email} ({u.email})</option>
                            ))}
                          </select>
                          <p className="text-xs text-gray-400 mt-1">Or type a new email for a guest order</p>
                          <input type="email" value={createOrderForm.email}
                            onChange={e => setCreateOrderForm(f => ({ ...f, email: e.target.value }))}
                            className="w-full p-2.5 border-2 border-gray-200 rounded-xl focus:border-primary focus:outline-none mt-1"
                            placeholder="or enter email manually" data-testid="order-email-input" />
                        </div>
                        <div>
                          <div className="flex items-center justify-between mb-2">
                            <label className="block text-sm font-medium">Products <span className="text-red-500">*</span></label>
                            <button type="button" onClick={addOrderLineItem}
                              className="text-xs font-semibold text-primary hover:underline"
                              data-testid="add-line-item-btn">
                              + Add Another Product
                            </button>
                          </div>
                          <div className="space-y-2">
                            {createOrderForm.line_items.map((li, idx) => {
                              const selectedProduct = products.find(p => p.product_id === li.product_id);
                              const isPiece = (selectedProduct?.unit || 'sqft').toLowerCase() === 'piece';
                              return (
                              <div key={idx} className="p-3 rounded-xl border-2 border-gray-100 bg-gray-50" data-testid={`line-item-${idx}`}>
                                <div className="flex items-center justify-between mb-2">
                                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Line #{idx + 1}</p>
                                  {createOrderForm.line_items.length > 1 && (
                                    <button type="button" onClick={() => removeOrderLineItem(idx)}
                                      className="text-xs text-red-500 hover:text-red-700 font-semibold"
                                      data-testid={`remove-line-item-${idx}`}>
                                      Remove
                                    </button>
                                  )}
                                </div>
                                <select
                                  value={li.product_id}
                                  onChange={e => updateOrderLineItem(idx, { product_id: e.target.value })}
                                  className="w-full p-2 border-2 border-gray-200 rounded-lg focus:border-primary focus:outline-none text-sm mb-2"
                                  data-testid={`line-item-product-${idx}`}
                                >
                                  <option value="">Select product...</option>
                                  {products.map(p => {
                                    const uLabel = (p.unit || 'sqft').toLowerCase() === 'piece' ? ' — per piece' : '';
                                    return (
                                      <option key={p.product_id} value={p.product_id}>{(p.display_name || p.name) + uLabel}</option>
                                    );
                                  })}
                                </select>
                                <div className="grid grid-cols-2 gap-2">
                                  <input type="number" value={li.sqft}
                                    onChange={e => updateOrderLineItem(idx, { sqft: e.target.value })}
                                    className="w-full p-2 border-2 border-gray-200 rounded-lg focus:border-primary focus:outline-none text-sm"
                                    placeholder={isPiece ? 'Qty (pieces)' : 'Sqft'}
                                    data-testid={`line-item-sqft-${idx}`} />
                                  <input type="number" step="0.01" value={li.price_per_sqft}
                                    onChange={e => updateOrderLineItem(idx, { price_per_sqft: e.target.value })}
                                    className="w-full p-2 border-2 border-gray-200 rounded-lg focus:border-primary focus:outline-none text-sm"
                                    placeholder={isPiece ? 'Price/piece (auto)' : 'Price/sqft (auto)'}
                                    data-testid={`line-item-price-${idx}`} />
                                </div>
                              </div>
                            );})}
                          </div>
                        </div>
                        <div>
                          <label className="block text-sm font-medium mb-1">Payment Status</label>
                          <div className="grid grid-cols-3 gap-2" data-testid="order-payment-status">
                            {[
                              { value: 'unpaid', label: 'Unpaid', desc: 'Customer pays later', color: 'border-red-300 bg-red-50 text-red-700' },
                              { value: 'paid_deposit', label: '30% Deposit', desc: 'Deposit received', color: 'border-amber-300 bg-amber-50 text-amber-700' },
                              { value: 'paid_full', label: 'Paid in Full', desc: '100% received', color: 'border-green-300 bg-green-50 text-green-700' },
                            ].map(opt => (
                              <button key={opt.value} type="button"
                                onClick={() => setCreateOrderForm(f => ({ ...f, payment_status: opt.value }))}
                                className={`p-3 rounded-xl border-2 text-center transition-all ${
                                  createOrderForm.payment_status === opt.value
                                    ? opt.color + ' ring-2 ring-offset-1 ring-current'
                                    : 'border-gray-200 hover:border-gray-300'
                                }`}
                              >
                                <p className="font-semibold text-sm">{opt.label}</p>
                                <p className="text-xs opacity-70 mt-0.5">{opt.desc}</p>
                              </button>
                            ))}
                          </div>
                        </div>
                        <div>
                          <label className="block text-sm font-medium mb-1">Container Assignment</label>
                          <div className="grid grid-cols-3 gap-2 mb-2" data-testid="order-container-mode">
                            {[
                              { value: 'auto', label: 'Auto Assign', desc: 'Best fit', color: 'border-blue-300 bg-blue-50 text-blue-700' },
                              { value: 'existing', label: 'Pick Container', desc: 'Choose one', color: 'border-purple-300 bg-purple-50 text-purple-700' },
                              { value: 'new', label: 'New Container', desc: 'Create fresh', color: 'border-teal-300 bg-teal-50 text-teal-700' },
                            ].map(opt => (
                              <button key={opt.value} type="button"
                                onClick={() => setCreateOrderForm(f => ({ ...f, container_mode: opt.value, container_id: '' }))}
                                className={`p-2.5 rounded-xl border-2 text-center transition-all ${
                                  createOrderForm.container_mode === opt.value
                                    ? opt.color + ' ring-2 ring-offset-1 ring-current'
                                    : 'border-gray-200 hover:border-gray-300'
                                }`}
                              >
                                <p className="font-semibold text-sm">{opt.label}</p>
                                <p className="text-xs opacity-70">{opt.desc}</p>
                              </button>
                            ))}
                          </div>
                          {createOrderForm.container_mode === 'existing' && (
                            <select
                              value={createOrderForm.container_id}
                              onChange={e => setCreateOrderForm(f => ({ ...f, container_id: e.target.value }))}
                              className="w-full p-2.5 border-2 border-gray-200 rounded-xl focus:border-primary focus:outline-none"
                              data-testid="order-container-select"
                            >
                              <option value="">Select container...</option>
                              {containers.filter(c => {
                                const st = (c.status || c.container_status || '').toLowerCase();
                                return st === 'filling' || st === 'active' || st === 'po_sent' || st === 'in_production';
                              }).map(c => {
                                const avail = (c.total_capacity || 26900) - (c.current_fill || 0);
                                const fillPct = Math.round(((c.current_fill || 0) / (c.total_capacity || 26900)) * 100);
                                return (
                                  <option key={c.container_id} value={c.container_id}>
                                    #{c.container_number || c.container_id} — {fillPct}% full ({avail.toLocaleString()} sqft available)
                                  </option>
                                );
                              })}
                            </select>
                          )}
                        </div>
                        <div>
                          <label className="block text-sm font-medium mb-1">Shipping Address (optional)</label>
                          <input type="text" value={createOrderForm.shipping_address.street}
                            onChange={e => setCreateOrderForm(f => ({ ...f, shipping_address: { ...f.shipping_address, street: e.target.value } }))}
                            className="w-full p-2.5 border-2 border-gray-200 rounded-xl focus:border-primary focus:outline-none mb-2"
                            placeholder="Street address" />
                          <div className="grid grid-cols-3 gap-2">
                            <input type="text" value={createOrderForm.shipping_address.city}
                              onChange={e => setCreateOrderForm(f => ({ ...f, shipping_address: { ...f.shipping_address, city: e.target.value } }))}
                              className="w-full p-2.5 border-2 border-gray-200 rounded-xl focus:border-primary focus:outline-none"
                              placeholder="City" />
                            <input type="text" value={createOrderForm.shipping_address.state}
                              onChange={e => setCreateOrderForm(f => ({ ...f, shipping_address: { ...f.shipping_address, state: e.target.value } }))}
                              className="w-full p-2.5 border-2 border-gray-200 rounded-xl focus:border-primary focus:outline-none"
                              placeholder="State" />
                            <input type="text" value={createOrderForm.shipping_address.zip}
                              onChange={e => setCreateOrderForm(f => ({ ...f, shipping_address: { ...f.shipping_address, zip: e.target.value } }))}
                              className="w-full p-2.5 border-2 border-gray-200 rounded-xl focus:border-primary focus:outline-none"
                              placeholder="ZIP" />
                          </div>
                        </div>
                        <div>
                          <label className="block text-sm font-medium mb-1">Admin Notes (optional)</label>
                          <input type="text" value={createOrderForm.notes}
                            onChange={e => setCreateOrderForm(f => ({ ...f, notes: e.target.value }))}
                            className="w-full p-2.5 border-2 border-gray-200 rounded-xl focus:border-primary focus:outline-none"
                            placeholder="Internal notes about this order" data-testid="order-notes-input" />
                        </div>
                      </div>
                      <div className="flex gap-3 mt-4">
                        <Button onClick={handleCreateOrder} disabled={creatingOrder} className="flex-1" data-testid="create-order-submit">
                          {creatingOrder ? <><RefreshCw className="w-4 h-4 mr-2 animate-spin" /> Creating...</> : <><Plus className="w-4 h-4 mr-2" /> Create Order</>}
                        </Button>
                        <Button variant="outline" onClick={resetCreateOrderForm} className="flex-1">Cancel</Button>
                      </div>
                    </>
                  )}
                </div>
              </div>
            )}

            {/* Edit Order Modal */}
            {editingOrder && (
              <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                <div className="bg-white rounded-2xl p-6 w-full max-w-md shadow-2xl max-h-[90vh] overflow-y-auto">
                  <h3 className="text-xl font-bold mb-4 flex items-center">
                    <Edit2 className="w-5 h-5 mr-2 text-blue-600" />
                    Edit Order Details
                  </h3>
                  
                  <div className="bg-gray-50 rounded-xl p-4 mb-4 text-sm space-y-2">
                    <div className="flex justify-between">
                      <span className="text-gray-500">Order ID</span>
                      <span className="font-mono">{editingOrder.pledge_id?.slice(-8)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500">Customer</span>
                      <span className="font-medium">{editingOrder.user_email}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500">Product</span>
                      <span className="font-medium">{editingOrder.product_name}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500">Price/{((editingOrder.line_items?.[0]?.unit) || products.find(p => p.product_id === editingOrder.product_id)?.unit || 'sqft') === 'piece' ? 'piece' : 'sqft'}</span>
                      <span className="font-medium">${parseFloat(editingOrder.price_per_sqft || 0).toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500">Order Total</span>
                      <span className="font-bold text-primary">${parseFloat(editingOrder.total_price || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                    </div>
                  </div>

                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-semibold mb-2 text-blue-700">Price per {((editingOrder.line_items?.[0]?.unit) || products.find(p => p.product_id === editingOrder.product_id)?.unit || 'sqft') === 'piece' ? 'Piece' : 'Sqft'} ($)</label>
                      <input
                        type="number"
                        step="0.01"
                        value={editOrderForm.price_per_sqft}
                        onChange={e => setEditOrderForm(f => ({ ...f, price_per_sqft: e.target.value }))}
                        className="w-full p-3 border-2 border-blue-200 rounded-xl focus:border-blue-500 focus:outline-none text-lg font-bold"
                        placeholder={((editingOrder.line_items?.[0]?.unit) || products.find(p => p.product_id === editingOrder.product_id)?.unit || 'sqft') === 'piece' ? 'e.g. 26.47' : 'e.g. 1.23'}
                        data-testid="edit-order-price-input"
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        Current: ${parseFloat(editingOrder.price_per_sqft || 0).toFixed(2)}/{((editingOrder.line_items?.[0]?.unit) || products.find(p => p.product_id === editingOrder.product_id)?.unit || 'sqft') === 'piece' ? 'piece' : 'sqft'}
                      </p>
                    </div>

                    <div>
                      <label className="block text-sm font-semibold mb-2">{((editingOrder.line_items?.[0]?.unit) || products.find(p => p.product_id === editingOrder.product_id)?.unit || 'sqft') === 'piece' ? 'Pieces (Quantity)' : 'Square Footage'}</label>
                      <input
                        type="number"
                        step="0.01"
                        value={editOrderForm.sqft}
                        onChange={e => setEditOrderForm(f => ({ ...f, sqft: e.target.value }))}
                        className="w-full p-3 border-2 border-gray-200 rounded-xl focus:border-blue-500 focus:outline-none text-lg"
                        placeholder={((editingOrder.line_items?.[0]?.unit) || products.find(p => p.product_id === editingOrder.product_id)?.unit || 'sqft') === 'piece' ? 'e.g. 5' : 'e.g. 5528.22'}
                        data-testid="edit-order-sqft-input"
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        Current: {(() => {
                          const li = editingOrder.line_items?.[0];
                          const isPiece = (li?.unit || products.find(p => p.product_id === editingOrder.product_id)?.unit || 'sqft') === 'piece';
                          const val = isPiece ? (li?.quantity ?? li?.sqft ?? 0) : editingOrder.sqft;
                          return `${(val ?? 0).toLocaleString()} ${isPiece ? 'pcs' : 'sqft'}`;
                        })()}
                      </p>
                    </div>

                    {/* Price Recalculation Preview */}
                    {(editOrderForm.price_per_sqft || editOrderForm.sqft) && (
                      <div className="bg-blue-50 border border-blue-200 rounded-xl p-3 text-sm">
                        <p className="font-semibold text-blue-800 mb-2">Price Recalculation Preview:</p>
                        <p className="text-blue-700">
                          {parseFloat(editOrderForm.sqft || editingOrder.sqft).toLocaleString()} {((editingOrder.line_items?.[0]?.unit) || products.find(p => p.product_id === editingOrder.product_id)?.unit || 'sqft') === 'piece' ? 'pcs' : 'sqft'} × ${parseFloat(editOrderForm.price_per_sqft || editingOrder.price_per_sqft).toFixed(2)} = 
                          <span className="font-bold ml-1">
                            ${(parseFloat(editOrderForm.sqft || editingOrder.sqft) * parseFloat(editOrderForm.price_per_sqft || editingOrder.price_per_sqft)).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                          </span>
                        </p>
                      </div>
                    )}

                    <div>
                      <label className="block text-sm font-semibold mb-2">Amount Paid ($)</label>
                      <input
                        type="number"
                        step="0.01"
                        value={editOrderForm.deposit_paid}
                        onChange={e => setEditOrderForm(f => ({ ...f, deposit_paid: e.target.value }))}
                        className="w-full p-3 border-2 border-gray-200 rounded-xl focus:border-green-500 focus:outline-none text-lg"
                        placeholder="e.g. 6799.44"
                        data-testid="edit-order-paid-input"
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        Current paid: ${parseFloat(editingOrder.deposit_paid || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </p>
                    </div>

                    {(() => {
                      const newPrice = parseFloat(editOrderForm.price_per_sqft || editingOrder.price_per_sqft || 0);
                      const newSqft = parseFloat(editOrderForm.sqft || editingOrder.sqft || 0);
                      const newTotal = newSqft * newPrice;
                      const newPaid = parseFloat(editOrderForm.deposit_paid || editingOrder.deposit_paid || 0);
                      const remaining = newTotal - newPaid;
                      const isPaidInFull = remaining <= 0;
                      
                      return (
                        <div className={`${isPaidInFull ? 'bg-green-50 border-green-200' : 'bg-amber-50 border-amber-200'} border rounded-xl p-3 text-sm`}>
                          <div className="flex justify-between mb-1">
                            <span className="text-gray-600">New Total:</span>
                            <span className="font-semibold">${newTotal.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                          </div>
                          <div className="flex justify-between mb-1">
                            <span className="text-gray-600">Amount Paid:</span>
                            <span className="font-semibold text-green-600">${newPaid.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                          </div>
                          <div className="flex justify-between border-t pt-1 mt-1">
                            <span className="font-semibold">Balance:</span>
                            <span className={`font-bold ${isPaidInFull ? 'text-green-600' : 'text-red-600'}`}>
                              {isPaidInFull ? 'PAID IN FULL' : `$${remaining.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
                            </span>
                          </div>
                          {isPaidInFull && newPaid >= newTotal && (
                            <p className="text-xs text-green-700 mt-2">
                              Status will be auto-updated to "paid in full"
                            </p>
                          )}
                        </div>
                      );
                    })()}

                    {/* Company Info for Invoice */}
                    <div className="border-t pt-4 mt-4">
                      <p className="text-sm font-semibold text-gray-700 mb-3 flex items-center">
                        <Building className="w-4 h-4 mr-2" />
                        Company Info (for Invoice)
                      </p>
                      <div className="grid grid-cols-2 gap-3">
                        <div className="col-span-2">
                          <input
                            type="text"
                            value={editOrderForm.company_name}
                            onChange={e => setEditOrderForm(f => ({ ...f, company_name: e.target.value }))}
                            className="w-full p-2 border border-gray-200 rounded-lg text-sm focus:border-blue-500 focus:outline-none"
                            placeholder="Company Name"
                            data-testid="edit-order-company-name"
                          />
                        </div>
                        <div className="col-span-2">
                          <input
                            type="text"
                            value={editOrderForm.company_address}
                            onChange={e => setEditOrderForm(f => ({ ...f, company_address: e.target.value }))}
                            className="w-full p-2 border border-gray-200 rounded-lg text-sm focus:border-blue-500 focus:outline-none"
                            placeholder="Company Address"
                            data-testid="edit-order-company-address"
                          />
                        </div>
                        <div>
                          <input
                            type="text"
                            value={editOrderForm.company_phone}
                            onChange={e => setEditOrderForm(f => ({ ...f, company_phone: e.target.value }))}
                            className="w-full p-2 border border-gray-200 rounded-lg text-sm focus:border-blue-500 focus:outline-none"
                            placeholder="Phone"
                            data-testid="edit-order-company-phone"
                          />
                        </div>
                        <div>
                          <input
                            type="text"
                            value={editOrderForm.company_tax_id}
                            onChange={e => setEditOrderForm(f => ({ ...f, company_tax_id: e.target.value }))}
                            className="w-full p-2 border border-gray-200 rounded-lg text-sm focus:border-blue-500 focus:outline-none"
                            placeholder="Tax ID / EIN"
                            data-testid="edit-order-company-taxid"
                          />
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="flex gap-3 mt-6">
                    <Button
                      onClick={handleSaveOrderEdit}
                      disabled={savingOrder}
                      className="flex-1 bg-blue-600 hover:bg-blue-700"
                      data-testid="save-order-edit"
                    >
                      {savingOrder ? (
                        <><RefreshCw className="w-4 h-4 mr-2 animate-spin" /> Saving...</>
                      ) : (
                        <><Check className="w-4 h-4 mr-2" /> Save Changes</>
                      )}
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => setEditingOrder(null)}
                      className="flex-1"
                    >
                      Cancel
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
        {activeTab === 'containers' && (<>
          <div className="space-y-6">
            <h2 className="text-2xl font-bold">Containers ({containers.length})</h2>
            <div className="space-y-6">
              {containers.map((container) => {
                const fillPct = Math.round((container.current_fill / container.total_capacity) * 100);
                const currentStatus = container.container_status || 'filling';
                const currentIdx = STATUS_FLOW.findIndex(s => s.key === currentStatus);
                const poSent = container.po_sent || currentIdx >= 1;

                return (
                  <div key={container.container_id} className="bg-white rounded-2xl shadow-sm p-6 border" data-testid={`container-card-${container.container_id}`}>
                    {/* Header */}
                    <div className="flex justify-between items-start mb-4">
                      <div>
                        <h3 className="text-xl font-bold">Container #{container.container_number}</h3>
                        <p className="text-sm text-gray-500">
                          {container.container_size} | {container.origin_port} → {container.destination_port}
                          {container.factory_location && (
                            <span className="ml-2 px-2 py-0.5 bg-purple-50 text-purple-700 rounded text-xs font-medium">
                              {container.factory_location}
                            </span>
                          )}
                        </p>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className={`px-3 py-1 rounded-full text-xs font-bold text-white ${STATUS_FLOW[currentIdx]?.color || 'bg-gray-400'}`}>
                          {STATUS_FLOW[currentIdx]?.label || currentStatus}
                        </span>
                        <span className="text-sm text-gray-500">{container.participants} orders</span>
                        <button
                          onClick={() => { setEditingContainer(container); setContainerEditForm({ total_capacity: container.total_capacity, current_fill: container.current_fill, container_size: container.container_size || '', carrier: container.carrier || '', carrier_tracking_number: container.carrier_tracking_number || '' }); }}
                          className="px-2 py-1 text-xs text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50"
                          data-testid={`edit-container-${container.container_id}`}
                        >
                          Edit Sqft
                        </button>
                      </div>
                    </div>

                    {/* Fill Progress */}
                    <div className="mb-4">
                      <div className="flex justify-between text-sm mb-1">
                        <span>Fill: {fillPct}%</span>
                        <span>{container.current_fill?.toLocaleString()} / {container.total_capacity?.toLocaleString()} sqft</span>
                      </div>
                      <div className="h-3 bg-gray-200 rounded-full overflow-hidden">
                        <div className="h-full bg-primary rounded-full transition-all" style={{ width: `${fillPct}%` }} />
                      </div>
                    </div>

                    {/* Status Timeline */}
                    <div className="flex items-center gap-1 mb-4 overflow-x-auto pb-2">
                      {STATUS_FLOW.map((step, i) => {
                        const isActive = i <= currentIdx;
                        const isCurrent = i === currentIdx;
                        return (
                          <div key={step.key} className="flex items-center flex-shrink-0">
                            <div className={`flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium transition-colors ${
                              isCurrent ? `${step.color} text-white` :
                              isActive ? 'bg-green-100 text-green-700' :
                              'bg-gray-100 text-gray-400'
                            }`}>
                              {isActive && i < currentIdx && <CheckCircle className="w-3 h-3" />}
                              {step.label}
                            </div>
                            {i < STATUS_FLOW.length - 1 && <ChevronRight className="w-3 h-3 text-gray-300 mx-0.5" />}
                          </div>
                        );
                      })}
                    </div>

                    {/* Container Info */}
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-sm mb-4 p-3 bg-gray-50 rounded-xl">
                      <div>
                        <span className="text-gray-500 text-xs block mb-1">Factory</span>
                        <select
                          value={container.factory_id || ''}
                          onChange={e => handleAssignFactory(container.container_id, e.target.value)}
                          disabled={assigningFactory === container.container_id}
                          className="w-full p-1.5 border rounded-lg text-sm bg-white focus:border-primary focus:outline-none"
                          data-testid={`assign-factory-${container.container_id}`}
                        >
                          <option value="">No Factory</option>
                          {factories.map(f => <option key={f.factory_id} value={f.factory_id}>{f.name}</option>)}
                        </select>
                      </div>
                      {container.tracking_number && (
                        <div><span className="text-gray-500 text-xs">Tracking</span><p className="font-medium">{container.tracking_number}</p></div>
                      )}
                      <div><span className="text-gray-500 text-xs">Est. Arrival</span><p className="font-medium">{container.arrival_date || '—'}</p></div>
                    </div>

                    {/* Action Area */}
                    <div className="border-t pt-4 space-y-3">
                      {/* Generate PO (available anytime, not gated by fill %) */}
                      {!poSent && (
                        <div className="p-4 bg-blue-50 border border-blue-200 rounded-xl">
                          <h4 className="font-semibold text-blue-800 mb-3 flex items-center gap-2"><Send className="w-4 h-4" /> Generate Production Order</h4>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
                            <select value={containerStatusForm.factory_id}
                              onChange={e => {
                                setContainerStatusForm(f => ({...f, factory_id: e.target.value}));
                                if (e.target.value) loadPoPreview(container.container_id);
                              }}
                              className="p-2.5 border-2 border-gray-200 rounded-lg text-sm focus:border-primary focus:outline-none" data-testid={`po-factory-${container.container_id}`}>
                              <option value="">Select Factory *</option>
                              {factories.map(f => <option key={f.factory_id} value={f.factory_id}>{f.name}</option>)}
                            </select>
                            <input placeholder="Notes / special instructions (optional)" value={containerStatusForm.notes}
                              onChange={e => setContainerStatusForm(f => ({...f, notes: e.target.value}))}
                              className="p-2.5 border-2 border-gray-200 rounded-lg text-sm focus:border-primary focus:outline-none" />
                          </div>

                          {/* PO Preview Table */}
                          {poPreview && poPreview.containerId === container.container_id && (
                            <div className="mb-3">
                              <h5 className="text-sm font-semibold text-gray-700 mb-2">PO Summary</h5>
                              <div className="bg-white rounded-lg border overflow-hidden">
                                <table className="w-full text-sm">
                                  <thead className="bg-gray-100">
                                    <tr>
                                      <th className="px-3 py-2 text-left font-medium text-gray-600">Internal SKU</th>
                                      <th className="px-3 py-2 text-left font-medium text-gray-600">Product</th>
                                      <th className="px-3 py-2 text-right font-medium text-gray-600">Total Sqft</th>
                                    </tr>
                                  </thead>
                                  <tbody className="divide-y">
                                    {(poPreview.line_items || []).map((item, i) => (
                                      <tr key={i} className="hover:bg-gray-50">
                                        <td className="px-3 py-2 font-mono text-xs text-amber-700">{item.internal_sku || '—'}</td>
                                        <td className="px-3 py-2">{item.product_name}</td>
                                        <td className="px-3 py-2 text-right font-semibold">{item.total_sqft?.toLocaleString()}</td>
                                      </tr>
                                    ))}
                                  </tbody>
                                  <tfoot className="bg-gray-50 font-bold">
                                    <tr>
                                      <td className="px-3 py-2" colSpan={2}>Container Total</td>
                                      <td className="px-3 py-2 text-right">{poPreview.total_sqft?.toLocaleString()} sqft</td>
                                    </tr>
                                  </tfoot>
                                </table>
                              </div>
                              <p className="text-xs text-gray-400 mt-1">{poPreview.order_count} orders aggregated by product</p>
                            </div>
                          )}

                          <div className="flex gap-3 flex-wrap">
                            {poPreview && poPreview.containerId === container.container_id && (
                              <Button variant="outline" onClick={() => downloadPoCsv(container.container_id)} data-testid={`download-csv-${container.container_id}`}>
                                <FileDown className="w-4 h-4 mr-2" /> Download PO (CSV)
                              </Button>
                            )}
                            <Button onClick={() => handleSendPO(container.container_id)}
                              disabled={sendingPO === container.container_id || !containerStatusForm.factory_id}
                              className="bg-blue-600 hover:bg-blue-700" data-testid={`send-po-${container.container_id}`}>
                              {sendingPO === container.container_id ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
                              {sendingPO === container.container_id ? 'Sending...' : 'Place Order & Notify Customers'}
                            </Button>
                          </div>
                          {/* Warn if factory has no contact email */}
                          {containerStatusForm.factory_id && (() => {
                            const f = factories.find(x => x.factory_id === containerStatusForm.factory_id);
                            return f && !f.email ? (
                              <div className="mt-2 p-3 bg-amber-50 border border-amber-200 rounded-xl text-xs text-amber-700 flex items-start gap-2">
                                <span className="font-bold mt-0.5">!</span>
                                <span><strong>{f.name}</strong> has no contact email set — supplier will NOT be notified. Add it under the <strong>Factories</strong> tab.</span>
                              </div>
                            ) : null;
                          })()}
                        </div>
                      )}

                      {/* Advance Status */}
                      {poSent && currentIdx < STATUS_FLOW.length - 1 && (
                        <div className="p-4 bg-gray-50 rounded-xl">
                          <h4 className="font-semibold mb-2">Advance Status</h4>
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-3">
                            {STATUS_FLOW[currentIdx + 1]?.key === 'on_vessel' && (
                              <input placeholder="Tracking Number" value={containerStatusForm.tracking_number}
                                onChange={e => setContainerStatusForm(f => ({...f, tracking_number: e.target.value}))}
                                className="p-2 border rounded-lg text-sm" data-testid={`tracking-${container.container_id}`} />
                            )}
                            <input placeholder="Est. Delivery Date" type="date" value={containerStatusForm.estimated_delivery}
                              onChange={e => setContainerStatusForm(f => ({...f, estimated_delivery: e.target.value}))}
                              className="p-2 border rounded-lg text-sm" />
                            <input placeholder="Notes (optional)" value={containerStatusForm.notes}
                              onChange={e => setContainerStatusForm(f => ({...f, notes: e.target.value}))}
                              className="p-2 border rounded-lg text-sm" />
                          </div>
                          <div className="flex gap-3 flex-wrap">
                            <Button
                              onClick={() => handleUpdateContainerStatus(container.container_id, STATUS_FLOW[currentIdx + 1].key)}
                              disabled={updatingContainerStatus === container.container_id}
                              className={STATUS_FLOW[currentIdx + 1]?.color?.replace('bg-', 'bg-') + ' hover:opacity-90 text-white'}
                              data-testid={`advance-status-${container.container_id}`}
                            >
                              {updatingContainerStatus === container.container_id ?
                                <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> :
                                <ChevronRight className="w-4 h-4 mr-2" />
                              }
                              Mark as "{STATUS_FLOW[currentIdx + 1]?.label}" & Notify Customers
                            </Button>
                            <Button variant="outline"
                              onClick={() => handleResendPO(container.container_id)}
                              disabled={resendingPO === container.container_id}
                              data-testid={`resend-po-${container.container_id}`}
                            >
                              {resendingPO === container.container_id ?
                                <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> :
                                <Mail className="w-4 h-4 mr-2" />
                              }
                              {resendingPO === container.container_id ? 'Sending...' : 'Resend PO to Factory'}
                            </Button>
                          </div>
                        </div>
                      )}

                      {currentIdx === STATUS_FLOW.length - 1 && (
                        <div className="p-3 bg-green-50 border border-green-200 rounded-xl text-center">
                          <CheckCircle className="w-5 h-5 text-green-600 inline mr-2" />
                          <span className="font-semibold text-green-700">Container fully delivered!</span>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
              {containers.length === 0 && (
                <div className="bg-white rounded-2xl p-12 text-center text-gray-500">
                  <Ship className="w-12 h-12 mx-auto text-gray-300 mb-3" />
                  No containers yet.
                </div>
              )}
            </div>
          </div>

          {/* Container Edit Modal */}
          {editingContainer && (
            <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
              <div className="bg-white rounded-2xl shadow-xl p-6 w-full max-w-md">
                <h3 className="text-lg font-bold mb-4">Edit Container #{editingContainer.container_number}</h3>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-semibold mb-1">Total Capacity (sqft)</label>
                    <input
                      type="number"
                      value={containerEditForm.total_capacity || ''}
                      onChange={e => setContainerEditForm(f => ({ ...f, total_capacity: e.target.value }))}
                      className="w-full p-3 border-2 border-gray-200 rounded-xl focus:border-primary focus:outline-none"
                      placeholder="e.g. 26900"
                      data-testid="container-edit-capacity"
                    />
                    <p className="text-xs text-gray-500 mt-1">Max sqft the container can hold</p>
                  </div>
                  <div>
                    <label className="block text-sm font-semibold mb-1">Current Fill Override (sqft)</label>
                    <input
                      type="number"
                      value={containerEditForm.current_fill || ''}
                      onChange={e => setContainerEditForm(f => ({ ...f, current_fill: e.target.value }))}
                      className="w-full p-3 border-2 border-gray-200 rounded-xl focus:border-primary focus:outline-none"
                      placeholder="e.g. 5400"
                      data-testid="container-edit-fill"
                    />
                    <p className="text-xs text-gray-500 mt-1">Manually adjust fill if needed (e.g. after order cancellations)</p>
                  </div>
                  <div>
                    <label className="block text-sm font-semibold mb-1">Container Size</label>
                    <select
                      value={containerEditForm.container_size || ''}
                      onChange={e => setContainerEditForm(f => ({ ...f, container_size: e.target.value }))}
                      className="w-full p-3 border-2 border-gray-200 rounded-xl focus:border-primary focus:outline-none"
                      data-testid="container-edit-size"
                    >
                      <option value="">Select size</option>
                      <option value="20ft Standard">20ft Standard (~12,000 sqft)</option>
                      <option value="40ft Standard">40ft Standard (~26,900 sqft)</option>
                      <option value="40ft High Cube">40ft High Cube (~28,500 sqft)</option>
                      <option value="45ft High Cube">45ft High Cube (~30,000 sqft)</option>
                    </select>
                  </div>
                  <div className="border-t pt-4 mt-2">
                    <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">Carrier Tracking (visible to customers)</p>
                    <div className="space-y-3">
                      <div>
                        <label className="block text-sm font-semibold mb-1">Ocean Carrier</label>
                        <select
                          value={containerEditForm.carrier || ''}
                          onChange={e => setContainerEditForm(f => ({ ...f, carrier: e.target.value }))}
                          className="w-full p-3 border-2 border-gray-200 rounded-xl focus:border-primary focus:outline-none"
                          data-testid="container-edit-carrier"
                        >
                          <option value="">Select carrier</option>
                          <option value="MSC">MSC</option>
                          <option value="MAERSK">Maersk</option>
                          <option value="COSCO">COSCO</option>
                          <option value="CMA CGM">CMA CGM</option>
                          <option value="HAPAG-LLOYD">Hapag-Lloyd</option>
                          <option value="EVERGREEN">Evergreen</option>
                          <option value="OOCL">OOCL</option>
                          <option value="ONE">ONE</option>
                          <option value="YANG MING">Yang Ming</option>
                          <option value="ZIM">ZIM</option>
                          <option value="HMM">HMM</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-semibold mb-1">Carrier Tracking # (Container or BOL)</label>
                        <input
                          type="text"
                          value={containerEditForm.carrier_tracking_number || ''}
                          onChange={e => setContainerEditForm(f => ({ ...f, carrier_tracking_number: e.target.value.toUpperCase() }))}
                          className="w-full p-3 border-2 border-gray-200 rounded-xl focus:border-primary focus:outline-none font-mono"
                          placeholder="e.g. MEDU5911086"
                          data-testid="container-edit-carrier-tracking-number"
                        />
                        <p className="text-xs text-gray-500 mt-1">Customers will see this with a one-click link to the carrier's tracking page.</p>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="flex gap-3 mt-6">
                  <button
                    onClick={handleSaveContainerEdit}
                    className="flex-1 bg-primary text-white py-2 rounded-xl font-semibold hover:bg-primary/90"
                    data-testid="container-edit-save"
                  >Save Changes</button>
                  <button
                    onClick={() => setEditingContainer(null)}
                    className="flex-1 border border-gray-200 py-2 rounded-xl text-gray-600 hover:bg-gray-50"
                  >Cancel</button>
                </div>
              </div>
            </div>
          )}
        </>)}

        {/* Products Tab */}
        {activeTab === 'products' && (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <h2 className="text-2xl font-bold">Products ({products.length})</h2>
              <div className="flex gap-3">
                <Button
                  onClick={handleBulkAdjustPrices}
                  disabled={syncingPrices}
                  variant="outline"
                  className="border-blue-600 text-blue-600 hover:bg-blue-50"
                  data-testid="bulk-adjust-prices-btn"
                >
                  <DollarSign className="w-4 h-4 mr-2" /> Bulk Adjust Prices (+/-)
                </Button>
                <Button 
                  onClick={handleSyncPrices} 
                  disabled={syncingPrices}
                  className="bg-amber-600 hover:bg-amber-700"
                  data-testid="sync-prices-btn"
                >
                  {syncingPrices ? (
                    <><RefreshCw className="w-4 h-4 mr-2 animate-spin" /> Syncing...</>
                  ) : (
                    <><RefreshCw className="w-4 h-4 mr-2" /> Sync Prices</>
                  )}
                </Button>
                <Button onClick={() => { resetProductForm(); setShowProductForm(true); }} data-testid="add-product-btn">
                  <Plus className="w-4 h-4 mr-2" /> Add Product
                </Button>
              </div>
            </div>

            {/* Price Sync Info Banner */}
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
              <div className="flex items-start gap-3">
                <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-semibold text-amber-900">Current pricing (as of latest adjustment)</p>
                  <p className="text-xs text-amber-700 mt-1">
                    • Hardwood: $3.77 (non-member) / $2.70 (member) — 23.33 sqft/box
                    <br />• LVP/SPC: $2.45 (non-member) / $1.44 (member) — 37.78 sqft/box
                    <br /><br />Use <strong>Bulk Adjust Prices</strong> to raise/lower every product by a fixed amount. Use <strong>Sync Prices</strong> to reset to the baseline (edit code to change baseline).
                  </p>
                </div>
              </div>
            </div>

            {/* Add/Edit Product Form */}
            {showProductForm && (
              <div className="bg-white rounded-2xl shadow-lg p-6 border-2 border-primary/30" data-testid="product-form">
                <h3 className="text-lg font-bold mb-4">{editingProduct ? 'Edit Product' : 'Add New Product'}</h3>
                <form onSubmit={handleSaveProduct} className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Customer Display Name *</label>
                      <input value={productForm.name} onChange={e => setProductForm(p => ({...p, name: e.target.value}))}
                        className="w-full p-2.5 border-2 border-gray-200 rounded-lg focus:border-primary focus:outline-none"
                        placeholder="Midwest White Oak LVP 9' x 60.25&quot; 5mm 20 mil" data-testid="product-name-input" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Internal SKU / Code</label>
                      <input value={productForm.internal_sku} onChange={e => setProductForm(p => ({...p, internal_sku: e.target.value}))}
                        className="w-full p-2.5 border-2 border-gray-200 rounded-lg focus:border-primary focus:outline-none"
                        placeholder="Premium SPC Flooring 2123-5 (9&quot; x 60.25&quot;)" data-testid="product-sku-input" />
                    </div>
                  </div>

                  {/* UPC Barcode Section */}
                  <div className="border-t pt-4">
                    <h4 className="font-semibold mb-2 flex items-center">
                      <Tag className="w-4 h-4 mr-2" />
                      UPC Barcode
                    </h4>
                    <div className="flex gap-3 items-end">
                      <div className="flex-1">
                        <label className="block text-xs font-medium text-gray-500 mb-1">UPC-A Code (12 digits)</label>
                        <input 
                          value={productForm.upc_code} 
                          onChange={e => setProductForm(p => ({...p, upc_code: e.target.value}))}
                          className="w-full p-2.5 border-2 border-gray-200 rounded-lg focus:border-primary focus:outline-none font-mono"
                          placeholder="000001234567" 
                          maxLength={12}
                          data-testid="product-upc-input" 
                        />
                      </div>
                      {editingProduct && (
                        <>
                          {!productForm.upc_code ? (
                            <Button
                              type="button"
                              onClick={() => handleGenerateUPC(editingProduct.product_id)}
                              disabled={generatingUPC === editingProduct.product_id}
                              className="bg-purple-600 hover:bg-purple-700 whitespace-nowrap"
                              data-testid="generate-upc-btn"
                            >
                              {generatingUPC === editingProduct.product_id ? (
                                <><RefreshCw className="w-4 h-4 mr-1 animate-spin" /> Generating...</>
                              ) : (
                                <><Plus className="w-4 h-4 mr-1" /> Generate UPC</>
                              )}
                            </Button>
                          ) : (
                            <div className="flex gap-2">
                              <Button
                                type="button"
                                onClick={() => handleDownloadBarcode(editingProduct.product_id, 'barcode')}
                                className="bg-gray-600 hover:bg-gray-700 whitespace-nowrap"
                                title="Download barcode image"
                              >
                                <Download className="w-4 h-4 mr-1" /> Barcode
                              </Button>
                              <Button
                                type="button"
                                onClick={() => handleDownloadBarcode(editingProduct.product_id, 'label')}
                                className="bg-blue-600 hover:bg-blue-700 whitespace-nowrap"
                                title="Download printable label with product name"
                              >
                                <FileText className="w-4 h-4 mr-1" /> Label
                              </Button>
                            </div>
                          )}
                        </>
                      )}
                    </div>
                    <p className="text-xs text-gray-400 mt-1">
                      {productForm.upc_code 
                        ? "UPC assigned. You can download barcode or label images for printing."
                        : "Click 'Generate UPC' to create a unique barcode, or enter an existing UPC code."}
                    </p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                    <textarea value={productForm.description} onChange={e => setProductForm(p => ({...p, description: e.target.value}))}
                      className="w-full p-2.5 border-2 border-gray-200 rounded-lg focus:border-primary focus:outline-none" rows={2}
                      placeholder="Product description for customers" data-testid="product-desc-input" />
                  </div>

                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
                      <select value={productForm.category} onChange={e => setProductForm(p => ({...p, category: e.target.value}))}
                        className="w-full p-2.5 border-2 border-gray-200 rounded-lg focus:border-primary focus:outline-none" data-testid="product-category-select">
                        <option value="spc">SPC / LVP</option>
                        <option value="engineered_hardwood">Engineered Hardwood</option>
                        <option value="interior_doors">Interior Doors</option>
                        <option value="other">Other</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Factory</label>
                      <select value={productForm.factory_id} onChange={e => setProductForm(p => ({...p, factory_id: e.target.value}))}
                        className="w-full p-2.5 border-2 border-gray-200 rounded-lg focus:border-primary focus:outline-none" data-testid="product-factory-select">
                        <option value="">No Factory</option>
                        {factories.map(f => <option key={f.factory_id} value={f.factory_id}>{f.name}</option>)}
                      </select>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Sold By <span className="text-red-500">*</span></label>
                    <div className="flex gap-2">
                      <button type="button"
                        onClick={() => setProductForm(p => ({...p, unit: 'sqft'}))}
                        className={`flex-1 p-3 rounded-xl border-2 font-semibold text-sm transition-all ${productForm.unit === 'sqft' ? 'bg-primary text-white border-primary' : 'bg-white text-gray-700 border-gray-200'}`}
                        data-testid="product-unit-sqft-btn">
                        Per Square Foot
                      </button>
                      <button type="button"
                        onClick={() => setProductForm(p => ({...p, unit: 'piece'}))}
                        className={`flex-1 p-3 rounded-xl border-2 font-semibold text-sm transition-all ${productForm.unit === 'piece' ? 'bg-primary text-white border-primary' : 'bg-white text-gray-700 border-gray-200'}`}
                        data-testid="product-unit-piece-btn">
                        Per Piece
                      </button>
                    </div>
                    <p className="text-xs text-gray-400 mt-1">
                      {productForm.unit === 'piece'
                        ? 'Customers order by quantity of pieces. Does not contribute to container volume.'
                        : 'Customers order by square footage (default).'}
                    </p>
                  </div>

                  <div className="border-t pt-4">
                    <h4 className="font-semibold mb-2">Pricing (${productForm.unit === 'piece' ? '/piece' : '/sqft'})</h4>
                    <div className="grid grid-cols-2 gap-3 max-w-md">
                      <div>
                        <label className="block text-xs font-medium text-gray-500 mb-1">Non-Member</label>
                        <input type="number" step="0.01" value={productForm.tier_pricing.non_member}
                          onChange={e => setProductForm(p => ({...p, tier_pricing: {...p.tier_pricing, non_member: e.target.value}}))}
                          className="w-full p-2 border-2 border-gray-200 rounded-lg focus:border-primary focus:outline-none text-sm"
                          placeholder="0.00" data-testid="product-price-non_member" />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-500 mb-1">Member</label>
                        <input type="number" step="0.01" value={productForm.tier_pricing.elite}
                          onChange={e => setProductForm(p => ({...p, tier_pricing: {...p.tier_pricing, elite: e.target.value, basic: e.target.value, pro: e.target.value}}))}
                          className="w-full p-2 border-2 border-gray-200 rounded-lg focus:border-primary focus:outline-none text-sm"
                          placeholder="0.00" data-testid="product-price-member" />
                      </div>
                    </div>
                    <p className="text-xs text-gray-400 mt-1">Member price applies to all membership tiers.</p>
                  </div>

                  <div className="border-t pt-4">
                    <h4 className="font-semibold mb-2 flex items-center">
                      <DollarSign className="w-4 h-4 mr-1 text-red-500" />
                      Cost Price (${productForm.unit === 'piece' ? '/piece' : '/sqft'})
                    </h4>
                    <p className="text-xs text-gray-400 mb-2">Your actual cost per {productForm.unit === 'piece' ? 'piece' : 'sqft'} - used for profit analysis calculations.</p>
                    <div className="max-w-xs">
                      <input type="number" step="0.01" value={productForm.cost_price}
                        onChange={e => setProductForm(p => ({...p, cost_price: e.target.value}))}
                        className="w-full p-2 border-2 border-red-200 rounded-lg focus:border-red-500 focus:outline-none text-sm bg-red-50"
                        placeholder="e.g. 0.96" data-testid="product-cost-price" />
                    </div>
                  </div>

                  <div className="border-t pt-4">
                    <h4 className="font-semibold mb-2 flex items-center">
                      <DollarSign className="w-4 h-4 mr-1 text-blue-500" />
                      Retail Price (${productForm.unit === 'piece' ? '/piece' : '/sqft'})
                    </h4>
                    <p className="text-xs text-gray-400 mb-2">Comparable retail price (e.g., Home Depot, Lowe's) - shown to customers to display their savings.</p>
                    <div className="max-w-xs">
                      <input type="number" step="0.01" value={productForm.retail_price}
                        onChange={e => setProductForm(p => ({...p, retail_price: e.target.value}))}
                        className="w-full p-2 border-2 border-blue-200 rounded-lg focus:border-blue-500 focus:outline-none text-sm bg-blue-50"
                        placeholder="e.g. 3.49" data-testid="product-retail-price" />
                    </div>
                  </div>

                  <div className="border-t pt-4">
                    <h4 className="font-semibold mb-2">Specifications</h4>
                    <div className="grid grid-cols-3 gap-3">
                      {['dimensions', 'thickness', 'wear_layer'].map(spec => (
                        <div key={spec}>
                          <label className="block text-xs font-medium text-gray-500 mb-1 capitalize">{spec.replace('_', ' ')}</label>
                          <input value={productForm.specifications[spec] || ''}
                            onChange={e => setProductForm(p => ({...p, specifications: {...p.specifications, [spec]: e.target.value}}))}
                            className="w-full p-2 border-2 border-gray-200 rounded-lg focus:border-primary focus:outline-none text-sm"
                            placeholder={spec === 'dimensions' ? '9" x 60.25"' : spec === 'thickness' ? '5mm' : '20 mil'} />
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="border-t pt-4">
                    <h4 className="font-semibold mb-2">Box Packaging</h4>
                    <p className="text-xs text-gray-400 mb-2">Used to round customer orders to full boxes for accurate invoicing.</p>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs font-medium text-gray-500 mb-1">Sqft per Box</label>
                        <input type="number" step="0.01" value={productForm.sqft_per_box}
                          onChange={e => setProductForm(p => ({...p, sqft_per_box: e.target.value}))}
                          className="w-full p-2 border-2 border-gray-200 rounded-lg focus:border-primary focus:outline-none text-sm"
                          placeholder="37.78" data-testid="product-sqft-per-box" />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-500 mb-1">Pieces per Box</label>
                        <input type="number" step="1" value={productForm.pieces_per_box}
                          onChange={e => setProductForm(p => ({...p, pieces_per_box: e.target.value}))}
                          className="w-full p-2 border-2 border-gray-200 rounded-lg focus:border-primary focus:outline-none text-sm"
                          placeholder="10" data-testid="product-pieces-per-box" />
                      </div>
                    </div>
                  </div>

                  <div className="flex gap-3 pt-2">
                    <Button type="submit" className="bg-primary hover:bg-primary/90" data-testid="save-product-btn">
                      <Check className="w-4 h-4 mr-2" /> {editingProduct ? 'Update Product' : 'Create Product'}
                    </Button>
                    <Button type="button" variant="outline" onClick={resetProductForm}>Cancel</Button>
                  </div>
                </form>
              </div>
            )}

            {/* Product Cards */}
            <div className="grid gap-4">
              {products.map((product) => (
                <div key={product.product_id} className="bg-white rounded-2xl shadow-sm p-5 border hover:border-primary/30 transition-colors" data-testid={`product-card-${product.product_id}`}>
                  <div className="flex gap-4">
                    {/* Product Image */}
                    <div className="w-24 h-24 rounded-xl overflow-hidden bg-gray-100 flex-shrink-0">
                      {product.image_url ? (
                        <img src={product.image_url.startsWith('/api') ? `${BACKEND_URL}${product.image_url}` : product.image_url}
                          alt={product.name} className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-gray-300">
                          <ImageIcon className="w-8 h-8" />
                        </div>
                      )}
                    </div>

                    {/* Product Info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2">
                        <div>
                          <h3 className="font-bold text-base">{product.name}</h3>
                          {product.internal_sku && (
                            <p className="text-xs text-amber-700 bg-amber-50 inline-block px-2 py-0.5 rounded mt-0.5 font-mono">
                              SKU: {product.internal_sku}
                            </p>
                          )}
                          {product.upc_code && (
                            <p className="text-xs text-purple-700 bg-purple-50 inline-block px-2 py-0.5 rounded mt-0.5 ml-1 font-mono">
                              UPC: {product.upc_code}
                            </p>
                          )}
                        </div>
                        <div className="flex items-center gap-1 flex-shrink-0">
                          <button onClick={() => openEditProduct(product)} className="p-1.5 text-gray-400 hover:text-primary rounded-lg hover:bg-primary/5" data-testid={`edit-product-${product.product_id}`}>
                            <Edit2 className="w-4 h-4" />
                          </button>
                          <button onClick={() => handleDeleteProduct(product.product_id)} className="p-1.5 text-gray-400 hover:text-red-600 rounded-lg hover:bg-red-50" data-testid={`delete-product-${product.product_id}`}>
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>

                      <div className="flex flex-wrap items-center gap-3 mt-2 text-sm">
                        <span className="px-2 py-0.5 bg-blue-50 text-blue-700 rounded text-xs font-medium">
                          {product.category?.replace(/_/g, ' ')}
                        </span>
                        {product.factory_id && (
                          <span className="px-2 py-0.5 bg-purple-50 text-purple-700 rounded text-xs font-medium flex items-center gap-1">
                            <Factory className="w-3 h-3" /> {getFactoryName(product.factory_id)}
                          </span>
                        )}
                        <span className="text-gray-600">Non-Member: <strong>${product.base_price?.toFixed(2)}</strong>/{(product.unit || 'sqft') === 'piece' ? 'piece' : 'sqft'}</span>
                        <span className="text-green-600">Member: <strong>${(product.member_pricing?.member || product.member_base_price)?.toFixed(2)}</strong>/{(product.unit || 'sqft') === 'piece' ? 'piece' : 'sqft'}</span>
                        {product.cost_price > 0 && (
                          <span className="text-red-600">Cost: <strong>${product.cost_price?.toFixed(2)}</strong>/{(product.unit || 'sqft') === 'piece' ? 'piece' : 'sqft'}</span>
                        )}
                        {!product.cost_price && (
                          <span className="text-amber-500 text-xs">(No cost set)</span>
                        )}
                        {product.sqft_per_box && (product.unit || 'sqft') !== 'piece' && (
                          <span className="text-gray-500 text-xs">{product.sqft_per_box} sqft/box ({product.pieces_per_box || '?'} pcs)</span>
                        )}
                        {(product.unit || 'sqft') === 'piece' && (
                          <span className="text-blue-600 text-xs font-semibold">Sold per piece</span>
                        )}
                      </div>

                      {/* Images row */}
                      <div className="flex items-center gap-2 mt-2">
                        {(product.images || []).map((img, i) => (
                          <div key={i} className="relative group w-10 h-10 rounded overflow-hidden border">
                            <img src={img.startsWith('/api') ? `${BACKEND_URL}${img}` : img} alt="" className="w-full h-full object-cover" />
                            <button onClick={() => handleDeleteProductImage(product.product_id, img)}
                              className="absolute inset-0 bg-red-500/70 text-white opacity-0 group-hover:opacity-100 flex items-center justify-center transition-opacity">
                              <X className="w-3 h-3" />
                            </button>
                          </div>
                        ))}
                        <label className={`w-10 h-10 rounded border-2 border-dashed border-gray-300 flex items-center justify-center cursor-pointer hover:border-primary hover:bg-primary/5 transition-colors ${uploadingProductImage ? 'opacity-50' : ''}`}>
                          {uploadingProductImage ? (
                            <div className="w-4 h-4 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                          ) : (
                            <Upload className="w-4 h-4 text-gray-400" />
                          )}
                          <input type="file" accept="image/*" className="hidden" disabled={uploadingProductImage}
                            onChange={e => handleProductImageUpload(product.product_id, e.target.files[0])}
                            data-testid={`upload-image-${product.product_id}`} />
                        </label>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
              {products.length === 0 && (
                <div className="bg-white rounded-2xl shadow p-12 text-center">
                  <Package className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                  <p className="text-gray-500">No products yet. Click "Add Product" to create one.</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Factories Tab */}
        {activeTab === 'factories' && (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <h2 className="text-2xl font-bold">Factories ({factories.length})</h2>
              <Button onClick={() => { resetFactoryForm(); setShowFactoryForm(true); }} data-testid="add-factory-btn">
                <Plus className="w-4 h-4 mr-2" /> Add Factory
              </Button>
            </div>

            {/* Add/Edit Factory Form */}
            {showFactoryForm && (
              <div className="bg-white rounded-2xl shadow-lg p-6 border-2 border-primary/30" data-testid="factory-form">
                <h3 className="text-lg font-bold mb-4">{editingFactory ? 'Edit Factory' : 'Add New Factory'}</h3>
                <form onSubmit={handleSaveFactory} className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Factory Name *</label>
                      <input value={factoryForm.name} onChange={e => setFactoryForm(f => ({...f, name: e.target.value}))}
                        className="w-full p-2.5 border-2 border-gray-200 rounded-lg focus:border-primary focus:outline-none"
                        placeholder="e.g. Davis Dong Manufacturing" data-testid="factory-name-input" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Contact Person</label>
                      <input value={factoryForm.contact_name} onChange={e => setFactoryForm(f => ({...f, contact_name: e.target.value}))}
                        className="w-full p-2.5 border-2 border-gray-200 rounded-lg focus:border-primary focus:outline-none"
                        placeholder="Primary contact name" data-testid="factory-contact-input" />
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                      <input type="email" value={factoryForm.email} onChange={e => setFactoryForm(f => ({...f, email: e.target.value}))}
                        className="w-full p-2.5 border-2 border-gray-200 rounded-lg focus:border-primary focus:outline-none"
                        placeholder="factory@email.com" data-testid="factory-email-input" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
                      <input value={factoryForm.phone} onChange={e => setFactoryForm(f => ({...f, phone: e.target.value}))}
                        className="w-full p-2.5 border-2 border-gray-200 rounded-lg focus:border-primary focus:outline-none"
                        placeholder="+86-xxx-xxx" data-testid="factory-phone-input" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Location</label>
                      <input value={factoryForm.location} onChange={e => setFactoryForm(f => ({...f, location: e.target.value}))}
                        className="w-full p-2.5 border-2 border-gray-200 rounded-lg focus:border-primary focus:outline-none"
                        placeholder="Guangzhou, China" data-testid="factory-location-input" />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                    <textarea value={factoryForm.notes} onChange={e => setFactoryForm(f => ({...f, notes: e.target.value}))}
                      className="w-full p-2.5 border-2 border-gray-200 rounded-lg focus:border-primary focus:outline-none" rows={2}
                      placeholder="Internal notes about this factory" data-testid="factory-notes-input" />
                  </div>
                  <div className="flex gap-3">
                    <Button type="submit" className="bg-primary hover:bg-primary/90" data-testid="save-factory-btn">
                      <Check className="w-4 h-4 mr-2" /> {editingFactory ? 'Update Factory' : 'Add Factory'}
                    </Button>
                    <Button type="button" variant="outline" onClick={resetFactoryForm}>Cancel</Button>
                  </div>
                </form>
              </div>
            )}

            {/* Factory List */}
            <div className="grid gap-4">
              {factories.map(factory => (
                <div key={factory.factory_id} className="bg-white rounded-2xl shadow-sm p-5 border hover:border-primary/30 transition-colors" data-testid={`factory-card-${factory.factory_id}`}>
                  <div className="flex items-start justify-between">
                    <div>
                      <h3 className="font-bold text-lg flex items-center gap-2">
                        <Factory className="w-5 h-5 text-primary" /> {factory.name}
                      </h3>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-3 text-sm">
                        <div>
                          <p className="text-gray-500 text-xs">Contact</p>
                          <p className="font-medium">{factory.contact_name || '—'}</p>
                        </div>
                        <div>
                          <p className="text-gray-500 text-xs">Email</p>
                          <p className="font-medium">{factory.email || '—'}</p>
                        </div>
                        <div>
                          <p className="text-gray-500 text-xs">Phone</p>
                          <p className="font-medium">{factory.phone || '—'}</p>
                        </div>
                        <div>
                          <p className="text-gray-500 text-xs">Location</p>
                          <p className="font-medium">{factory.location || '—'}</p>
                        </div>
                      </div>
                      {factory.notes && <p className="text-sm text-gray-500 mt-2 italic">{factory.notes}</p>}
                      <p className="text-xs text-gray-400 mt-1">
                        Products: {products.filter(p => p.factory_id === factory.factory_id).length}
                      </p>
                    </div>
                    <div className="flex gap-1 flex-shrink-0">
                      <button onClick={() => openEditFactory(factory)} className="p-1.5 text-gray-400 hover:text-primary rounded-lg hover:bg-primary/5" data-testid={`edit-factory-${factory.factory_id}`}>
                        <Edit2 className="w-4 h-4" />
                      </button>
                      <button onClick={() => handleDeleteFactory(factory.factory_id)} className="p-1.5 text-gray-400 hover:text-red-600 rounded-lg hover:bg-red-50" data-testid={`delete-factory-${factory.factory_id}`}>
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
              {factories.length === 0 && (
                <div className="bg-white rounded-2xl shadow p-12 text-center">
                  <Factory className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                  <p className="text-gray-500">No factories added yet. Click "Add Factory" to get started.</p>
                </div>
              )}
            </div>
          </div>
        )}


        {/* Transactions Tab */}
        {activeTab === 'transactions' && (
          <div className="space-y-6">
            <h2 className="text-2xl font-bold">Payment Transactions ({transactions.length})</h2>
            <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-sm font-medium text-gray-500">Transaction ID</th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-gray-500">Order ID</th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-gray-500">Customer</th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-gray-500">Type</th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-gray-500">Method</th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-gray-500">Amount</th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-gray-500">Status</th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-gray-500">Date</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {transactions.map((txn, idx) => (
                      <tr key={idx} className="hover:bg-gray-50">
                        <td className="px-4 py-3 font-mono text-xs">{txn.transaction_id?.slice(-10) || txn.session_id?.slice(-10) || '-'}</td>
                        <td className="px-4 py-3 font-mono text-xs">{txn.order_id?.slice(-10) || txn.pledge_id?.slice(-10) || '-'}</td>
                        <td className="px-4 py-3 text-sm">{txn.user_email || txn.email || '-'}</td>
                        <td className="px-4 py-3 text-sm">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                            txn.type === 'full_payment' ? 'bg-green-100 text-green-800' : 'bg-blue-100 text-blue-800'
                          }`}>
                            {txn.type === 'full_payment' ? 'Full Payment' : txn.type === 'deposit' ? 'Deposit' : txn.order_type?.replace('_', ' ') || 'Payment'}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-sm">
                          <span className={`px-2 py-1 rounded-full text-xs ${
                            txn.payment_method === 'wire' ? 'bg-blue-50 text-blue-700' :
                            txn.payment_method === 'check' ? 'bg-purple-50 text-purple-700' :
                            'bg-gray-100 text-gray-700'
                          }`}>
                            {txn.payment_method === 'wire' ? 'Wire' : txn.payment_method === 'check' ? 'Check' : 'Card'}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-sm font-bold text-green-600">${(txn.amount || txn.total_amount || 0).toFixed(2)}</td>
                        <td className="px-4 py-3">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                            (txn.status === 'completed' || txn.payment_status === 'paid') ? 'bg-green-100 text-green-800' :
                            (txn.status === 'pending' || txn.payment_status === 'pending') ? 'bg-yellow-100 text-yellow-800' :
                            'bg-red-100 text-red-800'
                          }`}>
                            {txn.status || txn.payment_status || 'unknown'}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-500">
                          {txn.created_at ? new Date(txn.created_at).toLocaleDateString() : '-'}
                        </td>
                      </tr>
                    ))}
                    {transactions.length === 0 && (
                      <tr>
                        <td colSpan="8" className="px-4 py-8 text-center text-gray-500">
                          No transactions yet. Transactions are created when payments are verified.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* Profit Tab */}
        {activeTab === 'profit' && (
          <div className="space-y-6">
            <h2 className="text-2xl font-bold">Profit Analysis</h2>
            
            {/* Helper to get product cost */}
            {(() => {
              const getProductCost = (pledge) => {
                const product = products.find(p => p.product_id === pledge.product_id);
                const costPerSqft = product?.cost_price || 0;
                return (pledge.sqft || 0) * costPerSqft;
              };
              
              const paidPledges = pledges.filter(p => p.deposit_status === 'paid');
              const totalRevenue = paidPledges.reduce((sum, p) => sum + (p.deposit_paid || 0), 0);
              const totalCost = paidPledges.reduce((sum, p) => sum + getProductCost(p), 0);
              const grossProfit = totalRevenue - totalCost;
              const totalSqft = paidPledges.reduce((sum, p) => sum + (p.sqft || 0), 0);
              
              return (
                <>
                  {/* Summary Cards */}
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
                    <div className="bg-white rounded-2xl shadow p-6">
                      <p className="text-sm text-gray-500 mb-1">Total Revenue</p>
                      <p className="text-3xl font-bold text-green-600">
                        ${totalRevenue.toLocaleString(undefined, {minimumFractionDigits: 2})}
                      </p>
                    </div>
                    <div className="bg-white rounded-2xl shadow p-6">
                      <p className="text-sm text-gray-500 mb-1">Total Cost</p>
                      <p className="text-3xl font-bold text-red-600">
                        ${totalCost.toLocaleString(undefined, {minimumFractionDigits: 2})}
                      </p>
                      {totalCost === 0 && (
                        <p className="text-xs text-amber-600 mt-1">Set cost price in Products tab</p>
                      )}
                    </div>
                    <div className="bg-white rounded-2xl shadow p-6">
                      <p className="text-sm text-gray-500 mb-1">Gross Profit</p>
                      <p className={`text-3xl font-bold ${grossProfit >= 0 ? 'text-primary' : 'text-red-600'}`}>
                        ${grossProfit.toLocaleString(undefined, {minimumFractionDigits: 2})}
                      </p>
                    </div>
                    <div className="bg-white rounded-2xl shadow p-6">
                      <p className="text-sm text-gray-500 mb-1">Total Sqft Sold</p>
                      <p className="text-3xl font-bold text-gray-900">
                        {totalSqft.toLocaleString()}
                      </p>
                    </div>
                  </div>

                  {/* Per-Order Profit Table */}
                  <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-4 py-3 text-left text-sm font-medium text-gray-500">Order ID</th>
                            <th className="px-4 py-3 text-left text-sm font-medium text-gray-500">Customer</th>
                            <th className="px-4 py-3 text-left text-sm font-medium text-gray-500">Product</th>
                            <th className="px-4 py-3 text-left text-sm font-medium text-gray-500">Sqft</th>
                            <th className="px-4 py-3 text-left text-sm font-medium text-gray-500">Revenue</th>
                            <th className="px-4 py-3 text-left text-sm font-medium text-gray-500">Cost</th>
                            <th className="px-4 py-3 text-left text-sm font-medium text-gray-500">Profit</th>
                            <th className="px-4 py-3 text-left text-sm font-medium text-gray-500">Margin</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y">
                          {paidPledges.map((pledge, idx) => {
                            const revenue = pledge.deposit_paid || 0;
                            const product = products.find(p => p.product_id === pledge.product_id);
                            const costPerSqft = product?.cost_price || 0;
                            const cost = (pledge.sqft || 0) * costPerSqft;
                            const profit = revenue - cost;
                            const margin = revenue > 0 ? ((profit / revenue) * 100).toFixed(1) : 0;
                            
                            return (
                              <tr key={idx} className="hover:bg-gray-50">
                                <td className="px-4 py-3 font-mono text-xs">{pledge.order_id?.slice(-10) || pledge.pledge_id?.slice(-10)}</td>
                                <td className="px-4 py-3 text-sm">{pledge.user_email}</td>
                                <td className="px-4 py-3 text-sm">{pledge.product_name}</td>
                                <td className="px-4 py-3 text-sm">{pledge.sqft?.toLocaleString()}</td>
                                <td className="px-4 py-3 text-sm font-semibold text-green-600">${revenue.toFixed(2)}</td>
                                <td className="px-4 py-3 text-sm text-red-600">
                                  ${cost.toFixed(2)}
                                  {costPerSqft === 0 && <span className="text-amber-500 text-xs ml-1">(no cost set)</span>}
                                </td>
                                <td className={`px-4 py-3 text-sm font-bold ${profit >= 0 ? 'text-green-700' : 'text-red-700'}`}>
                                  ${profit.toFixed(2)}
                                </td>
                                <td className={`px-4 py-3 text-sm font-medium ${margin >= 20 ? 'text-green-600' : margin >= 0 ? 'text-yellow-600' : 'text-red-600'}`}>
                                  {cost > 0 ? `${margin}%` : '—'}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </>
              );
            })()}
          </div>
        )}
      </div>
    </div>
  );
}

export default Admin;
